<template>
  <div class="radio-tetra-panel" :class="{ 'radio-tetra-panel--talking': radioTalking }">
    <!-- Header -->
    <div class="rt-header">
      <div class="rt-header__title">
        <v-icon small color="var(--kln-accent)" class="rt-header__icon">mdi-radio-handheld</v-icon>
        <span class="rt-header__text">Radio TETRA</span>
        <span class="rt-header__live" :class="{ 'rt-header__live--on': hasActiveChannels }">
          <span class="rt-header__live-dot" />
          {{ hasActiveChannels ? 'LIVE' : 'OFF' }}
        </span>
      </div>
      <div class="rt-header__actions">
        <button
          type="button"
          class="rt-mic-indicator"
          :class="micIndicatorClass"
          v-auto-tooltip="micIndicatorTip"
          :disabled="micTesting"
          @click="onTestMic"
        >
          <v-icon x-small>{{ micIndicatorIcon }}</v-icon>
          <span v-if="micTesting" class="rt-mic-indicator__spin" />
        </button>
        <v-btn
          icon
          x-small
          v-auto-tooltip="'Cerrar panel'"
          @click="$emit('close')"
        >
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </div>
    </div>

    <!-- Modo tabs -->
    <div class="rt-modes">
      <button
        v-for="m in modes"
        :key="m.id"
        type="button"
        class="rt-mode-tab"
        :class="{ 'rt-mode-tab--active': radioTetraMode === m.id }"
        @click="onModeChange(m.id)"
      >
        <v-icon x-small left>{{ m.icon }}</v-icon>
        {{ m.label }}
      </button>
    </div>

    <!-- Target card (modo device): cuando ya hay un radio seleccionado -->
    <div
      v-if="radioTetraMode === 'device' && radioTetraTarget && radioTetraTarget.type === 'device'"
      class="rt-target"
    >
      <div class="rt-target__icon">
        <v-icon small color="var(--kln-accent)">mdi-car-emergency</v-icon>
      </div>
      <div class="rt-target__body">
        <div class="rt-target__name">{{ radioTetraTarget.name || radioTetraTarget.uniqueId }}</div>
        <div class="rt-target__meta">
          <span>{{ radioTetraTarget.uniqueId }}</span>
          <span v-if="radioTetraTarget.groupName"> · {{ radioTetraTarget.groupName }}</span>
        </div>
      </div>
      <v-btn
        x-small
        icon
        v-auto-tooltip="'Cambiar dispositivo'"
        @click="onClearTarget"
      >
        <v-icon x-small>mdi-pencil</v-icon>
      </v-btn>
    </div>

    <!-- Lista de radios privados (modo device, sin radio seleccionado) -->
    <div v-else-if="radioTetraMode === 'device'" class="rt-radio-list">
      <div class="rt-radio-list__bar">
        <input
          v-model="radioSearch"
          type="text"
          class="rt-radio-list__search"
          placeholder="Buscar radio por nombre, ISSI o grupo…"
        />
        <button
          type="button"
          class="rt-radio-list__refresh"
          v-auto-tooltip="'Refrescar lista'"
          @click="$emit('refresh-radios')"
        >
          <v-icon x-small>mdi-refresh</v-icon>
        </button>
      </div>
      <div class="rt-radio-list__hint">
        Radios · <strong>{{ filteredRadios.length }}</strong>
      </div>
      <div v-if="!radios.length" class="rt-radio-list__empty">
        No hay radios disponibles.
      </div>
      <div v-else class="rt-radio-list__grid">
        <div
          v-for="r in filteredRadios"
          :key="r.monitorId || r.kalanId"
          class="rt-radio-card"
          :class="{ 'rt-radio-card--disabled': !r.callable }"
          :title="r.callable ? '' : 'Sin ID intersistema configurado'"
          @click="onSelectRadio(r)"
        >
          <span class="rt-radio-card__dot" :class="r.online ? 'is-online' : 'is-offline'" />
          <div class="rt-radio-card__body">
            <div class="rt-radio-card__name">{{ r.name }}</div>
            <div class="rt-radio-card__meta">
              <span v-if="r.issi" class="rt-radio-card__issi">{{ r.issi }}</span>
              <span v-if="r.groupName" class="rt-radio-card__grp">· {{ r.groupName }}</span>
            </div>
          </div>
          <span class="rt-radio-card__type">{{ (r.provider || '').toUpperCase() }}</span>
        </div>
      </div>
    </div>

    <!-- Lista de canales (modo channel): una columna. Selección
         single-radio porque MCX no soporta multi-group por llamada. Los
         groups vienen de radioGroups/visibleGroups (catálogo del backend). -->
    <div v-if="radioTetraMode === 'channel'" class="rt-multi-list">
      <div class="rt-multi-list__hint">
        <span>
          Canales · <strong>{{ radioChannels.length }}</strong>
        </span>
        <span v-if="callActive" class="rt-multi-list__status">
          <v-icon x-small color="var(--kln-accent)">mdi-access-point-network</v-icon>
          canal abierto
        </span>
      </div>
      <div v-if="!radioChannels.length" class="rt-multi-list__empty">
        Cargando canales…
      </div>
      <div class="rt-multi-list__grid">
        <div
          v-for="g in radioChannels"
          :key="g._id || g.groupId"
          class="rt-multi-card"
          :class="{
            'rt-multi-card--checked': isGroupSelected(g),
            'rt-multi-card--disabled': isGroupDisabled(g),
          }"
          :title="groupDisabledReason(g)"
          @click="onSelectGroup(g)"
        >
          <div class="rt-multi-card__row">
            <v-icon small :color="isGroupSelected(g) ? 'var(--kln-accent)' : 'var(--kln-text-dim)'">
              {{ isGroupSelected(g) ? 'mdi-radiobox-marked' : 'mdi-radiobox-blank' }}
            </v-icon>
            <span class="rt-multi-card__short">{{ g.alias || g.name }}</span>
            <span class="rt-multi-card__type" :title="g.type">
              {{ (g.type || '').toUpperCase() }}
            </span>
          </div>
          <div v-if="g.alias && g.name && g.alias !== g.name" class="rt-multi-card__full">{{ g.name }}</div>
          <div class="rt-multi-card__meta">
            <span class="rt-multi-card__gid">#{{ g.groupId }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Participants chips (cuando se han agregado devices al canal) -->
    <div v-if="radioTetraParticipants.length" class="rt-participants">
      <div class="rt-participants__hint">En la conversación:</div>
      <div class="rt-participants__chips">
        <span
          v-for="p in radioTetraParticipants"
          :key="p.uniqueId"
          class="rt-chip"
        >
          <v-icon x-small color="var(--kln-accent)">mdi-account-voice</v-icon>
          {{ p.name }}
          <v-icon
            x-small
            class="rt-chip__close"
            @click.stop="onRemoveParticipant(p.uniqueId)"
          >mdi-close</v-icon>
        </span>
      </div>
    </div>

    <!-- Estado del canal -->
    <div class="rt-channel-state">
      <span class="rt-channel-state__dot" :class="'rt-channel-state__dot--' + channelStateClass" />
      <span class="rt-channel-state__label">{{ channelStateLabel }}</span>
      <span class="rt-channel-state__divider">|</span>
      <v-icon x-small color="var(--kln-text-dim)" class="mr-1">mdi-volume-medium</v-icon>
      <span class="rt-channel-state__activity">{{ radioTalking ? 'Tu transmisión' : (hasRecentRx ? 'Actividad reciente' : 'Silencio') }}</span>
    </div>

    <!-- Banner permiso de mic denegado: aparece fuera del PTT para que
         el operador entienda por qué no transcribe aún cuando habla. -->
    <div v-if="micPermission === 'denied'" class="rt-mic-denied">
      <v-icon x-small color="var(--kln-warning, #F39C12)">mdi-microphone-off</v-icon>
      Permiso de micrófono denegado.
      <button type="button" class="rt-mic-denied__retry" @click="onTestMic">
        Reintentar
      </button>
    </div>

    <!-- Hint del Time-Out Timer: el TOT cortó la TX por exceder el tiempo
         máximo continuo. Transitorio (~4s); el operador suelta y vuelve a
         pulsar para seguir hablando ("rekey to continue"). -->
    <div v-if="totHint" class="rt-tot-cutoff">
      <v-icon x-small color="var(--kln-warning, #F39C12)">mdi-timer-alert-outline</v-icon>
      Tiempo de transmisión excedido · suelta y vuelve a pulsar
    </div>

    <!-- Zona PTT compacta. Botón circular + hint contextual + meter del mic
         a la derecha del botón (no debajo) para no robar espacio al chat. -->
    <div class="rt-ptt-zone">
      <button
        type="button"
        class="rt-ptt"
        :class="{
          'rt-ptt--talking': radioTalking,
          'rt-ptt--connecting': connecting,
          'rt-ptt--demanding': floorDemanding,
          'rt-ptt--receiving': peerSpeaking,
          'rt-ptt--listening': inListenMode && !peerSpeaking && !radioTalking && !floorDemanding,
          'rt-ptt--disabled': !canTransmit,
        }"
        :disabled="!canTransmit || peerSpeaking"
        v-auto-tooltip="pttTooltip"
        @pointerdown.prevent="onPttDown"
        @pointerup.stop="onPttUp"
        @pointercancel="onPttUp"
        @contextmenu.prevent
      >
        <span class="rt-ptt__inner">
          <v-icon large color="var(--kln-text)">{{ (radioTalking || floorDemanding) ? 'mdi-microphone' : 'mdi-microphone-outline' }}</v-icon>
          <span class="rt-ptt__label">{{ pttLabel }}</span>
        </span>
      </button>

      <!-- Onda de sonido ÚNICA. Se mueve con tu mic mientras hablas
           (micBands) y con el audio entrante del peer cuando recibís
           (remoteBands). Sin barra "RX" separada ni "esperando peer":
           activeBands selecciona la fuente activa. -->
      <div
        v-if="callActive || micTesting"
        class="rt-waveform"
        :class="{
          'rt-waveform--talking': radioTalking,
          'rt-waveform--receiving': !radioTalking && hasRemoteBands,
          'rt-waveform--synthetic': (radioTalking || micTesting) && !hasMicBands,
        }"
        :title="waveformTitle"
      >
        <span
          v-for="i in waveformBarCount"
          :key="i"
          class="rt-waveform__bar"
          :style="{ height: ((activeBands[i - 1] || 0.05) * 100) + '%' }"
        />
      </div>
      <div class="rt-ptt-hint">
        <template v-if="connecting">
          <v-icon x-small color="var(--kln-text-dim)" class="mr-1">mdi-phone-in-talk</v-icon>
          Conectando…
        </template>
        <template v-else-if="!callActive">
          <v-icon x-small color="var(--kln-text-dim)" class="mr-1">mdi-phone-outline</v-icon>
          {{ isChannelMode ? 'Pulsa para abrir canal' : 'Pulsa para iniciar llamada' }}
        </template>
        <template v-else-if="radioTalking">
          <v-icon x-small color="var(--kln-accent, #16C4C7)" class="mr-1">mdi-microphone</v-icon>
          Transmitiendo · clic (o <kbd>Ctrl</kbd>+<kbd>Espacio</kbd>) para escuchar
        </template>
        <template v-else-if="floorDemanding">
          <v-icon x-small color="var(--kln-warning, #F39C12)" class="mr-1">mdi-account-voice</v-icon>
          Pidiendo voz al canal…
        </template>
        <template v-else-if="peerSpeaking">
          <v-icon x-small color="#2ECC71" class="mr-1">mdi-headphones</v-icon>
          Escuchando al peer
        </template>
        <template v-else>
          <v-icon x-small color="var(--kln-text-dim)" class="mr-1">mdi-swap-vertical</v-icon>
          Clic (o <kbd>Ctrl</kbd>+<kbd>Espacio</kbd>) para hablar
        </template>
      </div>

      <!-- Botón colgar/cerrar canal — solo visible cuando hay una llamada activa. -->
      <v-btn
        v-if="callActive"
        small
        depressed
        color="error"
        class="rt-hangup-btn"
        @click="onHangup"
      >
        <v-icon x-small left>{{ isChannelMode ? 'mdi-access-point-network-off' : 'mdi-phone-hangup' }}</v-icon>
        {{ isChannelMode ? 'Cerrar canal' : 'Colgar' }}
      </v-btn>
    </div>

    <!-- Transcripción en vivo: SOLO visible mientras el operador transmite.
         Va DEBAJO de la zona PTT (no arriba) para que aparecer/desaparecer NO
         empuje el botón "clic para hablar": el botón debe quedar fijo para
         poder volver a clicarlo en el mismo lugar. Al soltar el PTT el bloque
         desaparece y el texto se va al chat como entry TX. -->
    <div v-if="radioTalking" class="rt-live-transcript">
      <div class="rt-live-transcript__head">
        <span class="rt-live-transcript__dot" />
        <span class="rt-live-transcript__label">Transcribiendo en vivo</span>
        <span v-if="!speechSupported" class="rt-live-transcript__warn">
          Sin soporte de transcripción en este navegador
        </span>
        <span v-else-if="speechErr === 'no-speech'" class="rt-live-transcript__warn">
          No detecté voz
        </span>
        <span v-else-if="speechErr" class="rt-live-transcript__warn">
          {{ speechErr }}
        </span>
      </div>
      <div ref="liveTranscriptBox" class="rt-live-transcript__body">
        <span class="rt-live-transcript__final">{{ transcriptFinal }}</span>
        <span class="rt-live-transcript__interim">{{ transcriptInterim }}</span>
        <span v-if="!transcriptFinal && !transcriptInterim" class="rt-live-transcript__hint">
          Habla ahora…
        </span>
      </div>
    </div>

    <!-- Aviso: el servicio STT del audio RX no responde. El audio del peer
         se sigue recibiendo y registrando ("audio recibido"), pero no se
         transcribe hasta que el servicio /api/stt vuelva. -->
    <div v-if="callActive && sttRxUnavailable" class="rt-rx-unavailable">
      <v-icon x-small color="var(--kln-warning, #F39C12)">mdi-text-box-remove-outline</v-icon>
      Transcripción RX no disponible · el audio se recibe pero no se transcribe
    </div>

    <!-- Chat unificado tipo Slack: lista cronológica con TX (operador) y RX
         (otros devices del canal) entremezclados. Más antiguo arriba, más
         nuevo abajo. Auto-scroll al fondo cuando llega un mensaje nuevo. -->
    <div class="rt-chat" ref="chatBox">
      <div v-if="!chatActivities.length" class="rt-chat__empty">
        Sin actividad todavía.<br />
        Pulsa el botón para hablar.
      </div>
      <div
        v-for="entry in chatActivities"
        :key="entry.id"
        class="rt-chat__msg"
        :class="'rt-chat__msg--' + entry.direction.toLowerCase()"
      >
        <!-- Retrato del elemento que transmite. Solo aparece cuando el
             hablante se puede identificar: en modo CANAL el backend TETRA no
             manda el id del radio (ver _pushRxEntry) y ahí se conservan las
             iniciales, que es lo honesto — una foto equivocada sería peor que
             ninguna. -->
        <div class="rt-chat__avatar" :title="entry.callerName">
          <img
            v-if="entry.direction !== 'TX' && speakerPhoto(entry)"
            :src="speakerPhoto(entry)"
            class="rt-chat__avatar-img"
            alt=""
            @error="onSpeakerPhotoError(entry)"
          />
          <v-icon v-else-if="entry.direction === 'TX'" x-small color="white">mdi-account</v-icon>
          <span v-else class="rt-chat__avatar-init">{{ getInitials(entry.callerName) }}</span>
        </div>
        <div class="rt-chat__body">
          <div class="rt-chat__head">
            <span class="rt-chat__name">{{ entry.direction === 'TX' ? 'Tú (operador)' : (entry.callerName || '—') }}</span>
            <span class="rt-chat__channel">
              <v-icon x-small class="rt-chat__channel-icon">{{ entry.direction === 'TX' ? 'mdi-arrow-up' : 'mdi-arrow-down' }}</v-icon>
              {{ entry.channelName || entry.channelId || 'sin canal' }}
            </span>
            <span class="rt-chat__time">{{ formatTime(entry.timestamp) }}</span>
            <span class="rt-chat__dur">{{ formatDuration(entry.durationMs) }}</span>
          </div>
          <div class="rt-chat__content">
            <template v-if="entry.transcript">{{ entry.transcript }}</template>
            <span v-else-if="entry.direction === 'TX'" class="rt-chat__placeholder">
              <v-icon x-small>mdi-microphone-off</v-icon>
              sin transcripción
            </span>
            <span v-else class="rt-chat__placeholder">
              <v-icon x-small>mdi-volume-high</v-icon>
              audio recibido · transcripción pendiente
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Audio: selección de micrófono / altavoz (portado del legacy
         RadioCallBar). Sección fija arriba del footer, siempre visible. El
         bridge persiste la preferencia y la reaplica en cada llamada; cambiar
         dispositivo funciona también a mitad de llamada. -->
    <div class="rt-audio">
      <template v-if="micPermission === 'granted'">
        <!-- Micrófono -->
        <v-menu v-model="micMenuOpen" offset-y top left nudge-top="4" :close-on-content-click="true">
          <template #activator="{ on }">
            <div class="rt-audio__row" v-on="on" v-auto-tooltip="'Cambiar micrófono'">
              <v-icon x-small color="var(--kln-text-muted)" class="mr-1">mdi-microphone</v-icon>
              <span class="rt-audio__name">{{ micDeviceLabel }}</span>
              <v-icon x-small color="var(--kln-text-dim)">mdi-menu-down</v-icon>
            </div>
          </template>
          <div class="rt-audio__menu">
            <div class="rt-audio__menu-title">Micrófono</div>
            <div class="rt-audio__menu-list">
              <div
                v-for="d in audioDevices.mics"
                :key="d.deviceId"
                class="rt-audio__menu-item"
                :class="{ 'is-current': d.deviceId === currentMicId }"
                @click="pickMic(d.deviceId)"
              >
                <v-icon x-small class="rt-audio__check" :color="d.deviceId === currentMicId ? 'var(--kln-accent)' : 'var(--kln-text-dim)'">
                  {{ d.deviceId === currentMicId ? 'mdi-check' : 'mdi-circle-small' }}
                </v-icon>
                <span class="rt-audio__menu-label">{{ d.label }}</span>
              </div>
            </div>
          </div>
        </v-menu>
        <!-- Altavoz -->
        <v-menu v-model="speakerMenuOpen" offset-y top left nudge-top="4" :disabled="!speakerSupported" :close-on-content-click="true">
          <template #activator="{ on }">
            <div
              class="rt-audio__row"
              :class="{ 'rt-audio__row--disabled': !speakerSupported }"
              v-on="on"
              v-auto-tooltip="speakerSupported ? 'Cambiar altavoz' : 'Este navegador no permite cambiar el altavoz'"
            >
              <v-icon x-small color="var(--kln-text-muted)" class="mr-1">mdi-volume-high</v-icon>
              <span class="rt-audio__name">{{ speakerDeviceLabel }}</span>
              <v-icon v-if="speakerSupported" x-small color="var(--kln-text-dim)">mdi-menu-down</v-icon>
            </div>
          </template>
          <div class="rt-audio__menu">
            <div class="rt-audio__menu-title">Altavoz</div>
            <div class="rt-audio__menu-list">
              <div
                v-for="d in audioDevices.speakers"
                :key="d.deviceId"
                class="rt-audio__menu-item"
                :class="{ 'is-current': d.deviceId === currentSpeakerId }"
                @click="pickSpeaker(d.deviceId)"
              >
                <v-icon x-small class="rt-audio__check" :color="d.deviceId === currentSpeakerId ? 'var(--kln-accent)' : 'var(--kln-text-dim)'">
                  {{ d.deviceId === currentSpeakerId ? 'mdi-check' : 'mdi-circle-small' }}
                </v-icon>
                <span class="rt-audio__menu-label">{{ d.label }}</span>
              </div>
            </div>
          </div>
        </v-menu>
      </template>
      <!-- Sin permiso: Chrome oculta las etiquetas hasta concederlo. -->
      <button v-else type="button" class="rt-audio__permit" @click="requestAudioPermission">
        <v-icon x-small color="var(--kln-warning, #F39C12)" class="mr-1">mdi-microphone-question</v-icon>
        Permitir micrófono para elegir dispositivos
      </button>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import dayjs from 'dayjs'
import radioAudioBridge from '@/util/radioAudioBridge'
import ApiService from '@/util/api.service'
import sttService from '@/util/stt-service'
import { concatFloat32, float32ToWav, rmsOf } from '@/util/wav-encoder'
import {
  speechSupported,
  speechHelper,
  micLevelMeter,
  requestMicPermission,
  getMicPermissionState,
} from '@/components/map-v2/data/speech-recognition.js'
import {
  ensureDriverDirectory, isDriverDirectoryLoaded, getDriverInfoById, driverPhotoUrl,
} from '@/components/map-v2/data/driver-directory.js'
import { findConductorByRadioIdent } from '@/components/map-v2/data/conductor-links.js'

export default {
  name: 'RadioTetraPanel',

  props: {
    radios: { type: Array, default: function() { return [] } },
  },

  data() {
    return {
      modes: [
        { id: 'device',  label: 'Dispositivo', icon: 'mdi-car-emergency' },
        { id: 'channel', label: 'Canales',     icon: 'mdi-access-point-network' },
      ],
      // Despierta a speakerPhoto cuando el directorio de elementos termina de
      // cargar o cuando una foto resulta rota: el módulo es async y no
      // reactivo por sí mismo.
      driverDirTick: 0,
      radioSearch: '',
      pttStartedAt: 0,
      // ─── Speech recognition + mic ───
      speechSupported: speechSupported,
      transcriptInterim: '',  // texto interim del current PTT
      transcriptFinal: '',    // texto final acumulado del current PTT
      speechErr: null,        // string error si SpeechRecognition falla
      micPermission: 'unknown', // granted|denied|prompt|unknown
      micErr: null,           // string error específico del último intento mic
      micLevel: 0,            // 0..1 nivel actual del mic (alimentado por meter)
      micTesting: false,      // true durante el "Probar micrófono"
      // ─── Selección de dispositivos de audio (mic/altavoz) ───
      // Portado del legacy RadioCallBar. El bridge (radioAudioBridge) lista,
      // aplica y persiste; aquí solo la UI. Las etiquetas (label) solo vienen
      // tras conceder permiso (privacidad de Chrome).
      audioDevices: { mics: [], speakers: [] },
      currentMicId: '',
      currentSpeakerId: '',
      micMenuOpen: false,
      speakerMenuOpen: false,
      // setSinkId (cambiar altavoz) no está en todos los navegadores (Firefox).
      speakerSupported: (typeof HTMLMediaElement !== 'undefined'
        && typeof HTMLMediaElement.prototype.setSinkId === 'function'),
      activeActivityTab: 'tx', // 'tx' | 'rx' — solo afecta layout en panel angosto
      // Mutex: true mientras un _radioStartCall está en vuelo (entre el
      // tap y la respuesta del POST). Evita que un double-tap dispare
      // dos POST /v2/radio/calls paralelos creando llamadas duplicadas
      // al mismo grupo MCX.
      callStarting: false,
      micBands: [],             // array N=40 de floats 0..1 con intensidad por banda de freq
      // Bandas del audio entrante del peer (analyser remoto del bridge).
      // Las dibujamos en un segundo waveform debajo del local para que el
      // operador vea si el peer está hablando — un plano significa "no me
      // están respondiendo" o "el remoto está mute".
      remoteBands: [],
      waveformBarCount: 36,     // N barras del equalizer
      // rAF id del loop que lee de los analysers del bridge mientras hay
      // call activa. Se inicializa cuando callActive=true (watcher) y se
      // cancela cuando vuelve a false.
      bridgeAnalyserRaf: null,

      // ─── STT del audio RX (transcripción del peer) ───
      // El AudioContext, AudioWorkletNode y MediaStreamSource del rx
      // capture viven en data NO reactivo (markRaw effectively) porque
      // son objetos pesados que NO deben pasar por el observer de Vue.
      // sttBurstSamples acumula chunks Float32 hasta que el VAD detecta
      // fin de speech, momento en que se envía el burst al servicio STT.
      sttCtx: null,
      sttSource: null,
      sttWorklet: null,
      sttBurstSamples: [],
      sttBurstActive: false,
      sttLastVoiceAt: 0,
      // Bandera para mostrar "Transcribiendo..." en la UI mientras
      // un burst está en proceso de STT (POST + inferencia).
      sttTranscribing: false,
      // true cuando el backend STT del RX (/api/stt) no responde — se enciende
      // al fallar un POST de transcripción y se apaga al primer éxito. Reseteo
      // optimista al abrir la llamada. Gobierna el banner "Transcripción RX no
      // disponible" para que el operador sepa que es el servicio y no un bug.
      sttRxUnavailable: false,
      // true mientras llega audio del peer (radio hablando), con hangover de
      // 1.2s. Deriva del VAD del RX porque TETRA no envía RemoteSpeaking.
      // Inhabilita el PTT del operador (half-duplex) y alimenta peerSpeaking.
      peerAudioActive: false,
      // Group seleccionado en modo 'channel'. Usamos el groupId (no _id)
      // porque ese es el identificador que entiende el backend MCX/TETRA.
      // Se inicializa al primer group disponible cuando carga el catálogo.
      selectedGroupId: null,
      // Hint transitorio del corte por Time-Out Timer (TOT). Lo enciende el
      // watcher de activeRadioCall.totCutoffAt y se auto-apaga a los ~4s.
      totHint: false,
    }
  },

  computed: {
    ...mapGetters('map-v2', [
      'radioTetraTarget',
      'radioTetraMode',
      'radioTetraParticipants',
      'radioActivityLog',
    ]),
    // Conexión con el backend real (radioCall + radioGroups, integrados
    // desde el merge de edomex-test). El POC ya no usa mock.
    ...mapGetters('radioCall', { activeRadioCall: 'active' }),
    ...mapGetters('radioGroups', ['visibleGroups']),

    // Adaptador: el POC original consumía radioChannels (mock). Ahora
    // el catálogo real vive en radioGroups (WS-upserted desde backend).
    radioChannels() {
      return this.visibleGroups || []
    },

    // floor === 'Granted' es el equivalente a "transmitiendo" del modelo
    // MCX/TETRA. Cuando el operador hace demandFloor y el server responde
    // 'floor' con 'Granted', el bridge habilita el mic y comienza el audio
    // real. Mantenemos el nombre `radioTalking` para no romper toda la UI.
    radioTalking() {
      return !!(this.activeRadioCall && this.activeRadioCall.floor === 'Granted')
    },

    // Estado del lifecycle de la llamada: idle | alerting | connected | terminated
    callState() {
      return this.activeRadioCall ? this.activeRadioCall.state : 'idle'
    },

    // callId de la llamada activa (o null). Las transcripciones se ligan a
    // este id; al cambiar a un callId NUEVO limpiamos el chat en pantalla
    // (el historial persiste en IndexedDB). Ver watcher activeCallId.
    activeCallId() {
      return this.activeRadioCall ? this.activeRadioCall.callId : null
    },

    callActive() {
      // Excluye el estado transitorio 'terminated' (los ~500ms entre colgar/
      // sin-respuesta y el CLEAR del store). Así la UI de llamada activa
      // (botón Colgar, onda, label "CLIC PARA HABLAR") desaparece de inmediato
      // al terminar, sin el parpadeo que daba mala impresión. El objeto sigue
      // en el store hasta CLEAR, pero para la UI ya no es una llamada activa.
      return !!this.activeRadioCall && this.activeRadioCall.state !== 'terminated'
    },

    // True desde el INSTANTE del clic en Llamar (callStarting, antes de que el
    // POST /calls responda) hasta que el lifecycle entra en alerting. Da
    // feedback inmediato: sin esto la UI no cambiaba hasta que el POST
    // respondía y parecía que el botón no reaccionó.
    connecting() {
      return this.callStarting || this.callState === 'alerting'
    },

    // Onda única: fuente de las barras según quién tiene la palabra.
    // Hablando → tu mic (micBands); recibiendo → audio del peer
    // (remoteBands); en silencio → vacío (las barras caen al mínimo 0.05
    // vía el fallback en el :style).
    activeBands() {
      if ((this.radioTalking || this.micTesting) && this.hasMicBands) return this.micBands
      if (this.hasRemoteBands) return this.remoteBands
      return []
    },
    waveformTitle() {
      if (this.radioTalking) return 'Tu micrófono'
      if (this.micTesting) return 'Prueba de micrófono'
      if (this.hasRemoteBands) return 'Audio del peer'
      return 'En llamada'
    },

    // En modelo MCX una llamada apunta a UN peer (device o group). Ya no
    // hay multi-channel — eso era del mock. Mostramos el peer activo en
    // la card del target o como chip en el header.
    radioActiveChannelIds() {
      var a = this.activeRadioCall
      if (!a || !a.peerId) return []
      return [a.peerId]
    },

    // True cuando hay datos reales del mic (mic permitido + activo). Sin
    // esto, el waveform cae a animación CSS sintética.
    hasMicBands() {
      return this.micBands && this.micBands.length > 0
    },
    hasRemoteBands() {
      if (!this.remoteBands || !this.remoteBands.length) return false
      // Consideramos "señal real" si al menos una banda supera el floor 0.1
      // — los analysers WebAudio dan ruido base ~0.05 incluso en silencio.
      for (var i = 0; i < this.remoteBands.length; i++) {
        if (this.remoteBands[i] > 0.1) return true
      }
      return false
    },

    // True cuando hay una llamada activa o un group seleccionado listo
    // para llamar — controla el LED del header.
    hasActiveChannels() {
      return this.callActive || !!this.selectedGroupId
    },

    allMaxSelected() { return false },

    // canTransmit es true cuando hay un target válido (device o group)
    // O hay una llamada activa. En modo device usa el radioTetraTarget
    // (preseleccionado), en modo channel usa el selectedGroupId.
    canTransmit() {
      if (this.callActive) return true
      if (this.radioTetraMode === 'device') {
        return !!(this.radioTetraTarget && this.radioTetraTarget.uniqueId)
      }
      return !!this.selectedGroupId
    },

    // El "active channel" en el nuevo modelo es el group del activeCall
    // si existe, o el group seleccionado por el operador.
    activeChannelObjs() {
      var self = this
      if (this.callActive && this.activeRadioCall.peerId) {
        var peer = this.radioChannels.find(function(g) {
          return g._id === self.activeRadioCall.peerId
            || g.groupId === self.activeRadioCall.peerId
        })
        if (peer) return [peer]
      }
      if (this.selectedGroupId) {
        var g = this.radioChannels.find(function(c) {
          return c.groupId === self.selectedGroupId || c._id === self.selectedGroupId
        })
        if (g) return [g]
      }
      return []
    },

    // Mapeamos el lifecycle del call a un class/label visible.
    channelStateClass() {
      if (!this.callActive) return this.selectedGroupId ? 'ready' : 'idle'
      var s = this.callState
      if (s === 'terminated') return 'offline'
      if (s === 'alerting') return 'busy'
      // connected — depende del floor
      var floor = (this.activeRadioCall && this.activeRadioCall.floor) || 'None'
      if (floor === 'Granted') return 'transmit'
      if (floor === 'RemoteSpeaking') return 'remote'
      if (floor === 'Denied') return 'denied'
      if (floor === 'Demanding') return 'demanding'
      return 'available'
    },

    channelStateLabel() {
      switch (this.channelStateClass) {
        case 'idle':      return 'Sin canal'
        case 'ready':     return 'Listo'
        case 'busy':      return this.isChannelMode ? 'Abriendo canal…' : 'Marcando…'
        case 'available': return 'Escuchando'    // floor=None en call activa
        case 'transmit':  return 'Hablando'
        case 'remote':    return 'Peer hablando'
        case 'demanding': return 'Pidiendo voz…'
        case 'denied':    return 'Piso denegado'
        case 'offline':   return this.isChannelMode ? 'Canal cerrado' : 'Llamada terminada'
        default:          return 'Sin canal'
      }
    },

    hasRecentRx() {
      if (!this.radioActivityLog.length) return false
      var last = this.radioActivityLog[0]
      if (last.direction !== 'RX') return false
      var now = new Date().getTime()
      var ts = last.timestamp instanceof Date ? last.timestamp.getTime() : last.timestamp
      return (now - ts) < 30000
    },

    // ─── Activity log (chat) ───
    // Renderizado con el más RECIENTE arriba: el store ya guarda FIFO
    // (insert al inicio en PUSH_RADIO_ACTIVITY), así que devolvemos el
    // array tal cual sin reverse. El auto-scroll va al tope cuando llega
    // un mensaje nuevo (ver watcher radioActivityLog.length).
    chatActivities() {
      return this.radioActivityLog
    },
    // Mantenemos los dos derivados por compat con el contador del header
    // (LIVE chip + footer), si alguien los referencia desde afuera.
    txActivities() {
      return this.radioActivityLog.filter(function(e) { return e.direction === 'TX' })
    },
    rxActivities() {
      return this.radioActivityLog.filter(function(e) { return e.direction === 'RX' })
    },

    // ─── Labels de dispositivos de audio (sección inferior) ───
    micDeviceLabel() {
      return this._resolveDeviceLabel(this.audioDevices.mics, this.currentMicId, 'Micrófono')
    },
    speakerDeviceLabel() {
      if (!this.speakerSupported) return 'Predeterminado del sistema'
      return this._resolveDeviceLabel(this.audioDevices.speakers, this.currentSpeakerId, 'Altavoz')
    },

    // ─── Mic indicator (en header del panel) ───
    micIndicatorIcon() {
      if (!this.speechSupported && this.micPermission !== 'granted') return 'mdi-microphone-off'
      switch (this.micPermission) {
        case 'granted': return 'mdi-microphone-settings'
        case 'denied':  return 'mdi-microphone-off'
        case 'prompt':  return 'mdi-microphone-question'
        default:        return 'mdi-microphone-question'
      }
    },
    micIndicatorClass() {
      return 'rt-mic-indicator--' + this.micPermission +
        (this.speechSupported ? '' : ' rt-mic-indicator--no-speech')
    },
    micIndicatorTip() {
      var pieces = []
      switch (this.micPermission) {
        case 'granted': pieces.push('Micrófono permitido'); break
        case 'denied':  pieces.push('Sin permiso de micrófono'); break
        case 'prompt':  pieces.push('Permiso de micrófono pendiente'); break
        default:        pieces.push('Estado del micrófono desconocido')
      }
      if (!this.speechSupported) pieces.push('Transcripción no soportada en este navegador')
      pieces.push('Clic para probar')
      return pieces.join(' · ')
    },

    // True cuando el peer remoto tiene el floor — estamos recibiendo
    // audio del otro lado (half-duplex). El operador no puede pedir floor
    // mientras el remoto está hablando (depende del backend MCX, algunos
    // dejan demand floor → cola).
    peerSpeaking() {
      // No contar como "recibiendo" mientras el operador tiene o pide la
      // palabra (su propio mic no debe verse como peer).
      if (this.radioTalking || this.floorDemanding) return false
      // floor RemoteSpeaking (si el backend lo enviara) O audio RX entrante.
      // En TETRA el backend no manda RemoteSpeaking → peerAudioActive (VAD).
      return !!(this.activeRadioCall && this.activeRadioCall.floor === 'RemoteSpeaking')
        || this.peerAudioActive
    },

    // True desde el INSTANTE del clic en "clic para hablar" hasta que el
    // server otorga el floor (pasa a 'Granted'). demandFloor commitea
    // floor:'Demanding' de forma síncrona, así que esto da retro inmediata
    // en el botón (igual que el header "Pidiendo voz…"), antes de transmitir.
    floorDemanding() {
      return !!(this.activeRadioCall && this.activeRadioCall.floor === 'Demanding')
    },

    // En modo 'channel' (pestaña Canales) la metáfora es "abrir/cerrar canal",
    // no "llamar/colgar" (que aplica a la llamada directa a un dispositivo en
    // modo 'device'). Cambia sólo los verbos de los botones/hints.
    isChannelMode() {
      return this.radioTetraMode === 'channel'
    },

    // True cuando estamos en call activa pero nadie tiene el floor —
    // estado "escuchando" del operador. Es el estado por default tras
    // soltar el PTT en una llamada half-duplex. El backend puede
    // dejarlo en 'None' o emitir 'RemoteSpeaking' si el peer toma voz.
    inListenMode() {
      if (!this.callActive) return false
      var f = this.activeRadioCall && this.activeRadioCall.floor
      return f === 'None' || f === 'Demanding' || f === 'Denied'
    },

    // Tooltip + label dinámicos del PTT según estado del lifecycle MCX.
    // El modelo es half-duplex: pulsar para hablar / soltar para escuchar.
    // El operador debe entender que SOLTAR ≠ COLGAR. La llamada sigue
    // activa, solo se libera el floor para que el peer pueda hablar.
    pttTooltip() {
      if (!this.canTransmit) return 'Selecciona un canal o dispositivo'
      if (this.connecting) return 'Conectando…'
      if (!this.callActive) return this.isChannelMode ? 'Pulsar para abrir canal' : 'Pulsar para llamar'
      if (this.radioTalking) return 'Transmitiendo · suelta para escuchar'
      if (this.floorDemanding) return 'Pidiendo voz al canal…'
      if (this.peerSpeaking) return 'El peer está hablando'
      // listen mode (callActive + floor:None/Demanding/Denied)
      return 'Mantén presionado para hablar (o Ctrl+Espacio)'
    },
    pttLabel() {
      if (this.connecting) return 'CONECTANDO'
      if (this.floorDemanding) return 'PIDIENDO VOZ…'
      if (this.radioTalking) return 'TRANSMITIENDO'
      if (this.peerSpeaking) return 'RECIBIENDO'
      if (this.callActive) return 'MANTÉN PARA HABLAR'
      return this.isChannelMode ? 'ABRIR CANAL' : 'LLAMAR'
    },

    liveTranscript() {
      // Concatena lo final + lo interim para mostrar en vivo arriba del PTT.
      // El interim viene del SpeechRecognition antes de que el motor lo
      // "selle" como final — visualizarlo da feedback inmediato al operador.
      var f = (this.transcriptFinal || '').trim()
      var i = (this.transcriptInterim || '').trim()
      if (!f && !i) return ''
      return (f + ' ' + i).trim()
    },

    // Filtra la lista de radios por nombre / ISSI / grupo (case-insensitive).
    filteredRadios: function() {
      var q = (this.radioSearch || '').trim().toLowerCase()
      if (!q) return this.radios
      return this.radios.filter(function(r) {
        return String(r.name || '').toLowerCase().indexOf(q) !== -1
          || String(r.issi || '').toLowerCase().indexOf(q) !== -1
          || String(r.groupName || '').toLowerCase().indexOf(q) !== -1
      })
    },
  },

  watch: {
    radioChannels(groups) {
      // Auto-seleccionar el primer group habilitado al cargar el catálogo
      // — el operador puede cambiar después haciendo click en otra card.
      // No tocar selectedGroupId si ya hay uno Y sigue existiendo en la
      // nueva lista. Si el group desapareció (deshabilitado/eliminado vía
      // WS upsert), invalidamos para que el operador re-selecione.
      var currentExists = this.selectedGroupId && groups.some(function(g) {
        return g.groupId === this.selectedGroupId || g._id === this.selectedGroupId
      }, this)
      if (this.selectedGroupId && !currentExists) {
        this.selectedGroupId = null
      }
      if (groups.length && !this.selectedGroupId) {
        var firstUsable = groups.find(function(g) { return g.type !== 'tetra' }) || groups[0]
        if (firstUsable) this.selectedGroupId = firstUsable.groupId || firstUsable._id
      }
    },
    // Al abrir una NUEVA llamada (callId distinto y no nulo), limpiar el chat
    // en pantalla: las transcripciones están ligadas a la llamada. El historial
    // de la llamada anterior NO se pierde — quedó persistido en IndexedDB por
    // pushRadioActivity. Al colgar (callId → null) NO limpiamos: el operador
    // sigue viendo la transcripción hasta que abra otra llamada.
    activeCallId(now, prev) {
      if (now && now !== prev) {
        this.$store.commit('map-v2/CLEAR_RADIO_ACTIVITY')
      }
    },
    radioTalking(now, prev) {
      if (now && !prev) {
        this.pttStartedAt = new Date().getTime()
        this.transcriptInterim = ''
        this.transcriptFinal = ''
        this.speechErr = null
        // Reset del detector de voz: solo commiteamos una entry TX si el
        // operador REALMENTE habló (texto STT o audio de mic). Al abrir el
        // canal la llamada arranca con floor=Granted (hablando) por default;
        // sin esto, soltar/cerrar sin decir nada metía una entry vacía
        // "sin transcripción".
        this._txHadVoice = false
        this._startSpeechCapture()
      }
      if (!now && prev) {
        // COMMIT PRIMERO, stop después: leemos transcriptFinal/Interim
        // mientras el motor SpeechRecognition aún tiene su estado intacto.
        // Si invertimos, el último segmento puede no quedar reflejado en
        // this.transcript* al commit. _stopSpeechCapture() llama abort(), que
        // descarta los finales tardíos a propósito (parcharlos tras soltar
        // hacía que el mensaje "se siguiera actualizando aunque ya estuviera
        // abajo").
        this._commitTxEntry()
        this._stopSpeechCapture()
        this.pttStartedAt = 0
      }
    },

    // Auto-scroll del chat al TOPE cuando entra un mensaje nuevo. Como
    // el más reciente va arriba, el operador siempre debe ver lo último
    // sin tener que scrollear.
    'radioActivityLog.length'(now, prev) {
      if (now > (prev || 0)) this._scrollChatToTop()
    },

    // Si la llamada termina por cualquier motivo (hangup local, terminated
    // remoto del backend, timeout, corte de red) MIENTRAS manteníamos el
    // PTT, soltar la red de seguridad global del hold-to-talk: el floor ya
    // no existe y no debe quedar un listener de pointerup/blur colgado.
    callState(newVal, oldVal) {
      if (newVal === 'terminated' || newVal === 'idle') {
        this._pttHeld = false
        this._disarmGlobalPttRelease()
      }
      // Hold-through-connect: si el operador MANTUVO el PTT durante el
      // alerting→connected, tomar la palabra automáticamente al conectar
      // (el botón no re-dispara pointerdown solo porque la llamada conectó,
      // y la llamada conecta en escucha).
      if (newVal === 'connected' && oldVal !== 'connected'
        && this._pttHeld && this.canTransmit
        && !this.radioTalking && !this.floorDemanding) {
        this.demandFloor().catch(function() { /* ignore */ })
      }

      // ─── Audio feedback de transiciones del lifecycle ───
      // alerting → ringback pulsado mientras está marcando.
      // connected ← alerting → beep ascendente (conectó).
      // terminated ← connected/alerting → beep descendente (colgó/rechazo).
      if (newVal === 'alerting' && oldVal !== 'alerting') {
        this._startRingbackTone()
      }
      if (oldVal === 'alerting' && newVal !== 'alerting') {
        this._stopRingbackTone()
      }
      if (newVal === 'connected' && oldVal === 'alerting') {
        var self = this
        this._playBeep(660, 100, 0.18)
        setTimeout(function() { self._playBeep(880, 130, 0.18) }, 110)
      }
      if (newVal === 'terminated' && (oldVal === 'connected' || oldVal === 'alerting')) {
        var self2 = this
        this._playBeep(660, 100, 0.18)
        setTimeout(function() { self2._playBeep(440, 160, 0.18) }, 110)
        // Feedback "sin respuesta": si la llamada SONABA (alerting) y terminó
        // SIN que el operador colgara (localHangup), el gateway MCX no pudo
        // alcanzar el radio — apagado / fuera de cobertura / ocupado / rechazo.
        // En connected→terminated (fin normal o cuelgue) no alarmamos.
        var ac = this.activeRadioCall
        if (oldVal === 'alerting' && !(ac && ac.localHangup)) {
          // NO mostramos el endReason crudo (es un código del backend, inútil
          // para el operador). Mensaje genérico que cubre tanto "sonó y no
          // atendió" como "no disponible/no registrado" (rechazo rápido).
          if (window.VMA && window.VMA.showMessage) {
            window.VMA.showMessage({
              title: 'Sin respuesta',
              body: 'El dispositivo no contestó o no está disponible (apagado, fuera de cobertura o no registrado).',
              color: 'warning',
              icon: 'mdi-phone-cancel',
            })
          }
        }
      }
    },

    // El TOT cortó la transmisión por exceder el tiempo máximo. Mostramos un
    // hint transitorio en el panel (~4s) para que el operador entienda por qué
    // se cortó el mic y sepa que debe soltar y volver a pulsar. totCutoffAt es
    // un timestamp que cambia en cada corte, así el watcher re-dispara aunque
    // ya hubiera habido un corte antes en la misma llamada.
    'activeRadioCall.totCutoffAt'(ts) {
      if (!ts) return
      this.totHint = true
      if (this._totHintTimer) clearTimeout(this._totHintTimer)
      this._totHintTimer = setTimeout(() => { this.totHint = false }, 4000)
    },

    // Mostrar errores de audio del bridge (getUserMedia denegado,
    // dispositivo desconectado, SDP error, ICE timeout) al operador.
    // Sin este watcher, el panel mostraba "Marcando…" indefinido sin
    // explicación de por qué la llamada no progresaba.
    'activeRadioCall.audioError'(err) {
      if (err && window.VMA && window.VMA.showError) {
        window.VMA.showError({ title: 'Error de audio: ' + err })
      }
    },

    // Cuando hay una llamada activa (alerting/connected/etc.), conectar
    // el waveform y el monitor de stats RTP al bridge real. Esto reemplaza
    // al micLevelMeter propio del POC que abría un getUserMedia paralelo
    // y no reflejaba el audio que efectivamente se está enviando por RTP.
    callActive(now, prev) {
      if (now && !prev) {
        // Reset optimista: asumimos STT disponible hasta que un burst falle.
        this.sttRxUnavailable = false
        this._attachBridgeAnalysers()
        this._attachRxCapture()  // captura del audio RX para STT
        // Refrescar dispositivos: ya hay localStream → el mic actual sale del
        // track real, y los permisos ya están concedidos (labels completos).
        this._loadAudioDevices()
      }
      if (!now && prev) {
        this._detachBridgeAnalysers()
        this._detachRxCapture()
      }
    },

    // Recargar la lista al abrir cada menú (hardware pudo cambiar).
    micMenuOpen(v) { if (v) this._loadAudioDevices() },
    speakerMenuOpen(v) { if (v) this._loadAudioDevices() },
  },

  mounted() {
    // Guard del STT: solo true mientras el PTT está activo (ver
    // _startSpeechCapture/_stopSpeechCapture). Instance flag no reactivo.
    this._speechActive = false
    // Detector de voz del PTT actual: true si hubo audio de mic real.
    this._txHadVoice = false
    // Contador monótono de sesión para IDs únicos del activity log (TX/RX):
    // evita colisiones cuando dos entries caen en el mismo milisegundo (el id
    // es keyPath en IndexedDB, así que una colisión sobrescribiría la entry).
    this._radioSeq = 0
    // PTT físicamente mantenido (mouse/touch): permite tomar la palabra al
    // conectar si el operador mantuvo durante el alerting (hold-through-connect).
    this._pttHeld = false
    // Id del radio que habla, capturado DURANTE la ráfaga RX (el backend borra
    // remoteSpeaker al cesar, antes de que termine la transcripción → el push
    // tardío caería al nombre del canal).
    this._rxBurstSpeaker = null
    // Probe pasivo del permiso del mic — no abre prompt nativo, solo lee
    // el estado actual de navigator.permissions si existe. El operador
    // verá un indicador correcto desde el primer render del panel.
    var self = this
    getMicPermissionState().then(function(state) {
      self.micPermission = state
    })

    // Dispositivos de audio: cargar al montar + escuchar cambios de hardware
    // (conectar/quitar audífonos) para refrescar las listas/etiquetas.
    this._loadAudioDevices()
    if (navigator.mediaDevices && navigator.mediaDevices.addEventListener) {
      this._onAudioDeviceChange = function() { self._loadAudioDevices() }
      navigator.mediaDevices.addEventListener('devicechange', this._onAudioDeviceChange)
    }

    // Carga el catálogo real de groups (MCX + Tetra) del backend.
    // El store radioGroups hace fetch /v2/radio/groups una sola vez y
    // se mantiene fresco via WS upsert (handler radio.group en socketio-
    // handler.js → store action upsert).
    this.loadRadioGroups()

    // TX/RX reales: los eventos de radio (alerting/connected/floor +
    // RemoteSpeaking) llegan via WS radio.call al store radioCall.
    // - TX: watcher radioTalking (floor === 'Granted') → SpeechRecognition.
    // - RX: watcher callActive → _attachRxCapture engancha un AudioWorklet al
    //   remoteAudioEl del bridge, VADea el audio del peer y manda cada burst
    //   al servicio STT (/api/stt). Si el backend STT no responde, igual se
    //   crea la entry RX como "audio recibido · transcripción pendiente".

    // Retry bus del MicPermissionDialog. Cuando el operador concede el
    // permiso y hace "Reintentar", el dialog emite estos eventos por
    // $root con el device/group que se intentó llamar. Reiniciamos el
    // flujo desde _radioStartCall para que aproveche el permiso ya
    // concedido y haga el POST /v2/radio/calls.
    this._onRetryRadioDevice = function() { self._radioStartCall() }
    this._onRetryRadioGroup = function() { self._radioStartCall() }
    this.$root.$on('radio:retry-call-device', this._onRetryRadioDevice)
    this.$root.$on('radio:retry-call-group', this._onRetryRadioGroup)
  },

  beforeDestroy() {
    // Limpiar el timer del hint del TOT para que no dispare tras el unmount.
    if (this._totHintTimer) { clearTimeout(this._totHintTimer); this._totHintTimer = null }
    // Quitar el listener de devicechange de los dispositivos de audio.
    if (this._onAudioDeviceChange && navigator.mediaDevices && navigator.mediaDevices.removeEventListener) {
      navigator.mediaDevices.removeEventListener('devicechange', this._onAudioDeviceChange)
      this._onAudioDeviceChange = null
    }
    // Cleanup del retry bus para evitar listeners colgados que ejecuten
    // _radioStartCall después del unmount del panel.
    if (this._onRetryRadioDevice) {
      this.$root.$off('radio:retry-call-device', this._onRetryRadioDevice)
      this._onRetryRadioDevice = null
    }
    if (this._onRetryRadioGroup) {
      this.$root.$off('radio:retry-call-group', this._onRetryRadioGroup)
      this._onRetryRadioGroup = null
    }
    // Si el panel se desmonta mientras se mantenía el PTT, ceder el floor
    // ANTES de soltar la red: si no, el radio seguiría transmitiendo hasta el
    // timeout del backend. (No cuelga la llamada — eso es una acción aparte.)
    if (this.radioTalking || this.floorDemanding) {
      this.ceaseFloor().catch(function() { /* ignore */ })
    }
    this._pttHeld = false
    this._disarmGlobalPttRelease()
    // Detener loops del bridge si quedaron corriendo (callActive=true
    // al desmontar).
    this._detachBridgeAnalysers()
    this._detachRxCapture()
    // Parar el ringback si quedó sonando + cerrar el AudioContext de
    // beeps para no dejar el WebAudio activo después del unmount.
    this._stopRingbackTone()
    if (this._beepAudioCtx) {
      try { this._beepAudioCtx.close() } catch (_) {}
      this._beepAudioCtx = null
    }
    // Soltar cualquier captura activa de mic/speech para no dejar
    // tracks/contexts colgados al cerrar el panel mientras se transmitía.
    this._stopSpeechCapture()
  },

  methods: {
    ...mapActions('radioCall', {
      placeRadioCall: 'placeCall',
      demandFloor: 'demandFloor',
      ceaseFloor: 'ceaseFloor',
      hangupRadioCall: 'hangup',
      showMicPermissionModal: 'showMicPermissionModal',
    }),
    ...mapActions('radioGroups', { loadRadioGroups: 'load' }),

    onModeChange(modeId) {
      // Si hay una llamada activa, cambiar de modo (device ↔ channel)
      // implícitamente cambia el peer. Para evitar inconsistencia entre
      // selectedGroupId/radioTetraTarget y el active.peerId, requerimos
      // que el operador cuelgue primero.
      if (this.callActive && modeId !== this.radioTetraMode) {
        if (window.VMA && window.VMA.showMessage) {
          window.VMA.showMessage({
            title: 'Llamada activa',
            body: 'Termina la llamada actual antes de cambiar de modo.',
            color: 'warning',
          })
        }
        return
      }
      this.$store.dispatch('map-v2/setRadioTetraMode', modeId)
    },

    onClearTarget() {
      // Limpia el dispositivo objetivo y se queda en modo 'device' mostrando el
      // estado guía. Para elegir otro, el operador inicia la comunicación desde
      // el detalle del dispositivo en el mapa (acción "Comunicación TETRA").
      this.$store.dispatch('map-v2/setRadioTetraTarget', null)
      this.$store.dispatch('map-v2/setRadioTetraMode', 'device')
    },

    // Single-select de group (modelo MCX): el operador elige UN group y
    // solo puede hacer placeCall a ese. Cambiar de group durante una
    // llamada activa requiere colgar primero.
    isGroupSelected(g) {
      if (!g) return false
      var gid = g.groupId || g._id
      return this.selectedGroupId === gid
    },

    isGroupDisabled(g) {
      if (!g) return true
      // Tetra ya está soportado por el backend (merge de edomex-test: provider
      // por tipo + holderKind). Misma lógica que el legacy: solo se deshabilita
      // si hay una llamada activa contra OTRO group, hasta que el operador
      // cuelgue. La llamada de grupo se arma igual para mcx y tetra
      // (type/provider = g.type).
      if (this.callActive) {
        var activePeer = this.activeRadioCall && this.activeRadioCall.peerId
        if (activePeer && activePeer !== g._id && activePeer !== g.groupId) return true
      }
      return false
    },

    groupDisabledReason(g) {
      if (this.isGroupDisabled(g)) return 'Termina la llamada actual antes de cambiar de canal'
      return ''
    },

    onSelectGroup(g) {
      if (this.isGroupDisabled(g)) return
      this.selectedGroupId = g.groupId || g._id
    },

    // Compat con código existente (sidebar/footer/computeds que aún lo
    // referencian). El "canal activo" en el nuevo modelo es el del
    // activeRadioCall si existe, o el seleccionado por el operador.
    isChannelActive(id) {
      return this.selectedGroupId === id
    },

    onRemoveParticipant(uniqueId) {
      this.$store.dispatch('map-v2/removeRadioParticipant', uniqueId)
    },

    // ─── PTT hold-to-talk (mantener para hablar, como un radio real) ───
    // pointerdown = tomar la palabra; pointerup/cancel = soltarla. Pointer
    // events cubren mouse + touch + pen de forma uniforme.
    //  - Sin llamada → iniciar la llamada al target (conecta en ESCUCHA;
    //    el operador mantiene de nuevo para hablar una vez conectada).
    //  - Marcando (alerting) → ignorar (aún no hay floor disponible).
    //  - En llamada → demandFloor + armar la red de seguridad global.
    // Ctrl+Espacio replica el hold (keydown/keyup) desde MapV2.vue.
    onPttDown() {
      if (!this.canTransmit) return
      // Half-duplex: no se puede tomar la palabra mientras el peer transmite
      // (el botón ya está :disabled por peerSpeaking; este guard cubre la
      // ventana entre render y evento, y la ruta de teclado).
      if (this.peerSpeaking) return
      // Marcar held + armar la red de seguridad SIEMPRE (también al iniciar
      // llamada): así un release en cualquier lado (fuera del botón, blur de
      // ventana) limpia el estado, y si el operador MANTIENE durante el
      // alerting→connected, el watcher de callState toma la palabra al
      // conectar (el botón no re-dispara pointerdown solo por conectar).
      this._pttHeld = true
      this._armGlobalPttRelease()
      if (!this.callActive) {
        this._radioStartCall()
        return
      }
      if (this.callState === 'alerting') return
      // Ya tomando/pidiendo la palabra: no re-demandar (p.ej. pointerdown
      // repetido o teclado + mouse simultáneos).
      if (this.radioTalking || this.floorDemanding) return
      this.demandFloor().catch(function() { /* ignore */ })
    },

    // Soltar el PTT → ceder la palabra (pasar a escuchar). NO cuelga la
    // llamada. Se invoca desde pointerup/pointercancel del botón Y desde la
    // red de seguridad global (pointerup en document / blur de window) para
    // que NUNCA quede el mic "pegado" si sueltas fuera del botón o cambias
    // de ventana. Idempotente: ceaseFloor con floor!=Granted/Demanding es no-op.
    onPttUp() {
      this._pttHeld = false
      this._disarmGlobalPttRelease()
      if (!this.callActive) return
      if (this.radioTalking || this.floorDemanding) {
        this.ceaseFloor().catch(function() { /* ignore */ })
      }
    },

    // Mientras se mantiene el PTT, escuchamos el release a nivel global: un
    // pointerup fuera del botón (arrastraste el cursor afuera) o el blur de
    // la ventana (Alt+Tab) deben cortar la transmisión igual que soltar.
    _armGlobalPttRelease() {
      if (this._globalPttUp) return
      this._globalPttUp = () => this.onPttUp()
      document.addEventListener('pointerup', this._globalPttUp)
      window.addEventListener('blur', this._globalPttUp)
    },

    _disarmGlobalPttRelease() {
      if (!this._globalPttUp) return
      document.removeEventListener('pointerup', this._globalPttUp)
      window.removeEventListener('blur', this._globalPttUp)
      this._globalPttUp = null
    },

    // Inicia una llamada al target actual. Dos shapes:
    //  - Modo device: POST kind=direct con kalanId del device
    //  - Modo channel: POST kind=group con groupId del group seleccionado
    // Pre-flight: gate de mic permission con bridge.requestMicPermission;
    // si no, levanta el modal del store radioCall.
    async _radioStartCall() {
      // Mutex: si ya estamos iniciando una llamada (POST en vuelo),
      // ignorar taps adicionales. Evita placeCalls duplicados por
      // double-tap natural en tablet de operaciones.
      if (this.callStarting) return
      var target = this._currentRadioTarget()
      if (!target) return
      this.callStarting = true
      try {
        var ok = await radioAudioBridge.requestMicPermission()
        if (!ok) {
          this.showMicPermissionModal(target.kind === 'group'
            ? { group: target.raw }
            : { device: target.raw })
          return
        }
        // Sin overlay global de carga aquí: este placement incluye
        // bridge.open() (negociación WebRTC) y bloquear toda la UI mientras
        // tanto se quedaba PEGADO si la llamada terminaba en medio (radio
        // offline) — el finally nunca corría. El estado CONECTANDO del panel
        // + el ringback son el feedback correcto, sin bloquear el monitoreo.
        try {
          var body
          if (target.kind === 'group') {
            body = {
              kind: 'group',
              type: target.provider,
              groupId: target.id,
              callType: 1,
              callMode: 0,
              callPriority: 1,
              duplexFlag: 0,
            }
          } else {
            body = {
              kind: 'direct',
              type: target.provider,
              kalanId: String(target.kalanId),
              callType: 0,
              callMode: 0,
              callPriority: 1,
            }
          }
          var resp = await ApiService({ url: '/v2/radio/calls', method: 'post', data: body })
          var callId = resp && resp.callId
          if (!callId) throw new Error('Sin callId del backend')
          await this.placeRadioCall({
            callId: callId,
            peerName: target.name,
            peerId: target.id,
            provider: target.provider,
          })
        } catch (err) {
          // Mensaje del backend si existe (body.code/body.message); cae
          // a err.message si no llegó cuerpo, o "Error desconocido" como
          // último recurso. Mejor info al operador que un genérico.
          var body2 = err && err.response && err.response.data
          var detail = (body2 && (body2.message || body2.code))
            || (err && err.message)
            || 'Error desconocido'
          window.VMA && window.VMA.showError && window.VMA.showError({ title: 'No se pudo iniciar la llamada: ' + detail })
        }
      } finally {
        this.callStarting = false
      }
    },

    // Resuelve el target actual del panel.
    // Modo 'device': prioriza radioTetraTarget (preseleccionado desde el
    // drawer o búsqueda). Modo 'channel': usa el primer group seleccionado
    // (sólo permitimos uno a la vez ahora — MCX no soporta multi-group).
    _currentRadioTarget() {
      if (this.radioTetraMode === 'device') {
        var t = this.radioTetraTarget
        // kalanId = ISSI (attributes.interSystemId) o id numerico de fallback;
        // sin el, el POST responde KALAN_ID_NOT_MAPPED. provider sale de la
        // categoria del device (no se hardcodea 'mcx').
        if (!t || !t.kalanId) return null
        return {
          kind: 'direct',
          provider: t.provider,
          id: t.deviceId,        // peerId (numerico, para casar markers en el mapa)
          kalanId: t.kalanId,    // lo que se manda al backend
          name: t.name || t.uniqueId || String(t.deviceId),
          raw: t,
        }
      }
      // Channel mode — buscar el group seleccionado (selectedGroupId local)
      var groupId = this.selectedGroupId
      if (!groupId) return null
      var group = this.radioChannels.find(function(g) { return g.groupId === groupId || g._id === groupId })
      if (!group) return null
      return {
        kind: 'group',
        provider: group.type || 'mcx',
        id: group.groupId,
        name: group.alias || group.name,
        raw: group,
      }
    },

    async onHangup() {
      if (!this.callActive) return
      try { await this.hangupRadioCall() } catch (_e) { /* ignore */ }
    },

    formatTime(ts) {
      if (!ts) return ''
      var d = ts instanceof Date ? ts : new Date(ts)
      return dayjs(d).format('HH:mm:ss')
    },

    formatDuration(ms) {
      if (!ms || ms < 1000) return '<1s'
      var s = Math.round(ms / 1000)
      if (s < 60) return s + 's'
      var m = Math.floor(s / 60)
      var rem = s % 60
      return m + 'm ' + rem + 's'
    },

    // ─── Speech + Mic capture ───
    //
    // Llamado al inicio del PTT. Si hay soporte SpeechRecognition, arranca
    // el reconocimiento y bind los callbacks. En paralelo, intenta abrir
    // el meter del mic con getUserMedia. Si el operador rechaza el prompt,
    // capturamos el error y actualizamos micPermission para que el
    // indicador del header lo refleje.
    _startSpeechCapture() {
      var self = this
      // Guard: solo mientras el PTT está activo procesamos callbacks del
      // motor. El motor puede entregar resultados/onend tardíos DESPUÉS de
      // soltar (latencia del engine); sin este flag esos eventos mutaban el
      // transcript y parchaban el mensaje ya commiteado ("se sigue
      // actualizando aunque ya esté abajo") e incluso seguían transcribiendo
      // sin PTT. Con el guard, todo evento fuera del PTT se ignora.
      this._speechActive = true
      if (this.speechSupported) {
        speechHelper.start({
          onResult: function(res) {
            if (!self._speechActive) return
            if (res.freshFinal) self.transcriptFinal += res.freshFinal
            self.transcriptInterim = res.interim || ''
            self._scrollTranscript()
          },
          onError: function(err) {
            self.speechErr = err
            if (err === 'not-allowed' || err === 'service-not-allowed') {
              self.micPermission = 'denied'
            }
          },
          onEnd: function() {
            // Sin auto-restart ni parches tardíos: el mensaje se finaliza una
            // sola vez en _commitTxEntry al soltar el PTT; abort() en
            // _stopSpeechCapture descarta cualquier final tardío del motor.
          },
        })
      }
      // Mic meter es independiente del reconocimiento: aunque speech no
      // exista, el meter sirve como confirmación visual de "el browser
      // está escuchando tu mic". El callback recibe (level, bands):
      // level → barra fina del PTT button, bands → waveform equalizer.
      micLevelMeter.start(function(lvl, bands) {
        self.micLevel = lvl
        self.micBands = bands || []
        // Umbral por encima del ruido ambiente típico: marca que hubo voz
        // real en este PTT. Usado por _commitTxEntry para no crear entries
        // vacías cuando el operador no habló.
        if (lvl > 0.18) self._txHadVoice = true
      }, { bandCount: this.waveformBarCount }).then(function(ok) {
        if (ok) {
          self.micPermission = 'granted'
          self.micErr = null
        } else {
          if (self.micPermission === 'unknown') self.micPermission = 'denied'
        }
      })
    },

    _stopSpeechCapture() {
      // Desactivar el guard ANTES de abortar para que cualquier evento que
      // el engine entregue durante el teardown se ignore.
      this._speechActive = false
      // abort() (no stop()) detiene el motor de inmediato y descarta el audio
      // pendiente: sin esto, el reconocimiento graceful seguía escuchando el
      // mic y entregando finales tardíos tras soltar el PTT.
      if (this.speechSupported) speechHelper.abort()
      micLevelMeter.stop()
      this.micLevel = 0
      this.micBands = []
    },

    // Empuja al activity log la entrada TX del PTT recién finalizado, con la
    // transcripción acumulada (final + interim) en ese momento. Es el ÚNICO
    // punto donde se finaliza el mensaje TX: no hay parcheo posterior porque
    // abort() (en _stopSpeechCapture) descarta los finales tardíos del motor.
    _commitTxEntry() {
      if (!this.pttStartedAt) return
      var dur = new Date().getTime() - this.pttStartedAt
      if (dur < 200) dur = 200
      var full = ((this.transcriptFinal || '') + ' ' + (this.transcriptInterim || '')).trim()
      // No crear entry si el operador no habló: ni texto STT ni voz detectada
      // por el mic. Cubre el caso de abrir el canal (arranca hablando) y
      // soltar/cerrar sin transmitir nada → ya no aparece "sin transcripción".
      if (!full && !this._txHadVoice) return
      var id = 'tx-' + new Date().getTime() + '-' + (this._radioSeq++)
      this.$store.dispatch('map-v2/pushRadioActivity', {
        id: id,
        callId: this.activeRadioCall ? this.activeRadioCall.callId : null,
        direction: 'TX',
        channelId: this.radioActiveChannelIds[0] || null,
        channelName: this.activeChannelObjs[0] ? this.activeChannelObjs[0].shortName : '',
        callerId: 'OPERADOR',
        callerName: 'Tú',
        durationMs: dur,
        timestamp: new Date(),
        transcript: full || null,
      })
    },

    _scrollTranscript() {
      var self = this
      this.$nextTick(function() {
        var el = self.$refs.liveTranscriptBox
        if (el) el.scrollTop = el.scrollHeight
      })
    },

    // Scroll del chat al TOPE — el mensaje más reciente está arriba.
    // SimpleBar wrapea el contenedor scrollable en un div interno
    // (.simplebar-content-wrapper); si existe usamos ese, sino el ref
    // directo (que también funciona porque SimpleBar lo proxa).
    _scrollChatToTop() {
      var self = this
      this.$nextTick(function() {
        var el = self.$refs.chatBox
        if (el) el.scrollTop = 0
      })
    },

    // ─── Audio feedback ───
    // Beeps sintéticos generados con Web Audio API. Sin assets externos:
    // un Oscillator con envelope ADSR rápido suena como un beep limpio
    // de teléfono/radio. El AudioContext se crea on-demand y se reusa
    // entre beeps para no incurrir en latencia de inicialización.
    _playBeep(freq, durationMs, volume) {
      try {
        var AC = window.AudioContext || window.webkitAudioContext
        if (!AC) return
        if (!this._beepAudioCtx) this._beepAudioCtx = new AC()
        var ctx = this._beepAudioCtx
        // Algunos browsers (Chrome) suspenden el AudioContext hasta que
        // hay user gesture. resume() es no-op si ya está corriendo.
        if (ctx.state === 'suspended' && ctx.resume) {
          try { ctx.resume() } catch (_) {}
        }
        var osc = ctx.createOscillator()
        var gain = ctx.createGain()
        osc.connect(gain)
        gain.connect(ctx.destination)
        osc.type = 'sine'
        osc.frequency.value = freq
        var now = ctx.currentTime
        var dur = (durationMs || 200) / 1000
        var vol = (volume == null) ? 0.15 : volume
        // ADSR: 20ms attack, sustain plano, 60ms release para que no
        // suene un "click" al cortar el oscillator abruptamente.
        gain.gain.setValueAtTime(0, now)
        gain.gain.linearRampToValueAtTime(vol, now + 0.02)
        gain.gain.setValueAtTime(vol, now + Math.max(0, dur - 0.06))
        gain.gain.linearRampToValueAtTime(0, now + dur)
        osc.start(now)
        osc.stop(now + dur + 0.05)
      } catch (_) { /* ignore — no audio context, no problem */ }
    },

    // Ringback estilo línea telefónica: beep dual 440Hz+480Hz cada 1.5s
    // mientras la llamada está marcando ('alerting'). Volumen suave (0.12)
    // para no asustar al operador en tablets/audífonos.
    _startRingbackTone() {
      this._stopRingbackTone()
      var self = this
      var play = function() {
        self._playBeep(440, 350, 0.12)
        // Segundo tono en armónica para emular el ringback "americano"
        // (US: 440Hz + 480Hz simultáneo). En MCX/TETRA es solo señal
        // visual+sonora para el operador, no se transmite por RTP.
        setTimeout(function() { self._playBeep(480, 350, 0.10) }, 10)
      }
      play()
      this._ringbackTimer = setInterval(play, 1600)
    },

    _stopRingbackTone() {
      if (this._ringbackTimer) {
        clearInterval(this._ringbackTimer)
        this._ringbackTimer = null
      }
    },

    // ─── Bridge analyser loop ───
    // Lee directamente del AnalyserNode del bridge para alimentar los
    // dos waveforms (local y remoto). Esto es el ÚNICO source de verdad
    // del audio durante una call activa — el micLevelMeter propio del POC
    // sigue activo solo para el test fuera de call.
    _attachBridgeAnalysers() {
      var self = this
      var localAnalyser = radioAudioBridge.getAnalyser && radioAudioBridge.getAnalyser()
      var remoteAnalyser = radioAudioBridge.getRemoteAnalyser && radioAudioBridge.getRemoteAnalyser()
      // Buffer reusable para evitar GC pressure por allocations en rAF.
      var localData = localAnalyser ? new Uint8Array(localAnalyser.frequencyBinCount) : null
      var remoteData = remoteAnalyser ? new Uint8Array(remoteAnalyser.frequencyBinCount) : null

      // Limites empíricos para mapear bytes 0..255 → 0..1 (mismo cálculo
      // que el micLevelMeter del POC para que el feel visual sea coherente).
      var BAND_COUNT = this.waveformBarCount
      var USEFUL_BINS_LOCAL = localAnalyser ? Math.min(localAnalyser.frequencyBinCount, 90) : 0
      var USEFUL_BINS_REMOTE = remoteAnalyser ? Math.min(remoteAnalyser.frequencyBinCount, 90) : 0
      var BINS_PER_BAND_LOCAL = USEFUL_BINS_LOCAL ? Math.max(1, Math.floor(USEFUL_BINS_LOCAL / BAND_COUNT)) : 1
      var BINS_PER_BAND_REMOTE = USEFUL_BINS_REMOTE ? Math.max(1, Math.floor(USEFUL_BINS_REMOTE / BAND_COUNT)) : 1

      var tick = function() {
        // Re-resolvemos los analysers cada tick por si el bridge los
        // recreó (típico al reconectar mic/speaker en caliente).
        var la = radioAudioBridge.getAnalyser && radioAudioBridge.getAnalyser()
        var ra = radioAudioBridge.getRemoteAnalyser && radioAudioBridge.getRemoteAnalyser()
        if (la !== localAnalyser) {
          localAnalyser = la
          localData = la ? new Uint8Array(la.frequencyBinCount) : null
        }
        if (ra !== remoteAnalyser) {
          remoteAnalyser = ra
          remoteData = ra ? new Uint8Array(ra.frequencyBinCount) : null
        }

        if (localAnalyser && localData) {
          localAnalyser.getByteFrequencyData(localData)
          var lBands = new Array(BAND_COUNT)
          for (var b = 0; b < BAND_COUNT; b++) {
            var sum = 0
            var start = b * BINS_PER_BAND_LOCAL
            var end = Math.min(start + BINS_PER_BAND_LOCAL, USEFUL_BINS_LOCAL)
            for (var i = start; i < end; i++) sum += localData[i]
            var avg = sum / Math.max(1, (end - start))
            lBands[b] = Math.max(0.05, Math.min(1, avg / 140))
          }
          self.micBands = lBands
        }

        if (remoteAnalyser && remoteData) {
          remoteAnalyser.getByteFrequencyData(remoteData)
          var rBands = new Array(BAND_COUNT)
          for (var b2 = 0; b2 < BAND_COUNT; b2++) {
            var sum2 = 0
            var start2 = b2 * BINS_PER_BAND_REMOTE
            var end2 = Math.min(start2 + BINS_PER_BAND_REMOTE, USEFUL_BINS_REMOTE)
            for (var i2 = start2; i2 < end2; i2++) sum2 += remoteData[i2]
            var avg2 = sum2 / Math.max(1, (end2 - start2))
            rBands[b2] = Math.max(0.05, Math.min(1, avg2 / 140))
          }
          self.remoteBands = rBands
        }

        if (self.bridgeAnalyserRaf != null) {
          self.bridgeAnalyserRaf = requestAnimationFrame(tick)
        }
      }
      this.bridgeAnalyserRaf = requestAnimationFrame(tick)
    },

    _detachBridgeAnalysers() {
      if (this.bridgeAnalyserRaf != null) {
        cancelAnimationFrame(this.bridgeAnalyserRaf)
        this.bridgeAnalyserRaf = null
      }
      this.remoteBands = []
    },

    // ─── RX audio capture → STT ───
    // Engancha un AudioWorklet al remoteAudioEl.srcObject del bridge
    // para capturar el PCM del audio entrante del peer. Aplica VAD
    // simple (RMS > 0.02 con tail-off de 600ms) para detectar bursts
    // de speech. Cada burst se envía al servicio STT (whisper) y la
    // transcripción resultante se inyecta en el chat como entry RX.
    //
    // Diseño: usa AudioContext separado del bridge para no interferir
    // con su sample rate (16kHz para STT vs el del bridge que es 48kHz).
    // El stream remoto es el mismo MediaStream que el bridge reproduce
    // por el <audio> element.
    async _attachRxCapture() {
      // Guard anti-doble: si ya enganchamos (o un retry lo hizo), salir.
      if (this.sttCtx) return
      // Referencia local al AudioContext de ESTE attach. Tras el await de
      // addModule comparamos myCtx === this.sttCtx: si un _detachRxCapture (o un
      // re-attach por cambio rápido de llamada) corrió mientras esperábamos,
      // this.sttCtx ya no es el nuestro → abortamos sin pisar el otro attach ni
      // dereferenciar un contexto ya cerrado.
      let myCtx = null
      try {
        // remoteAudioEl lo crea bridge.open() de forma ASÍNCRONA y el
        // srcObject se asigna en el ontrack del peer. Cualquiera de los dos
        // puede no estar listo cuando el watcher de callActive dispara este
        // attach. Reintentamos hasta que la llamada termine (antes se desistía
        // si remoteAudioEl era null → el STT RX nunca arrancaba).
        var el = radioAudioBridge.remoteAudioEl
        var stream = el && el.srcObject
        if (!stream) {
          var self = this
          setTimeout(function() {
            if (self.callActive && !self.sttCtx) self._attachRxCapture()
          }, 500)
          return
        }

        // sampleRate 16000 = lo que espera Whisper. WebAudio resamplea
        // automáticamente desde la tasa del input (típicamente 48kHz).
        var AC = window.AudioContext || window.webkitAudioContext
        if (!AC) return
        myCtx = new AC({ sampleRate: 16000 })
        // Set ANTES del await: this.sttCtx actúa como mutex del guard de arriba.
        this.sttCtx = myCtx
        try { await myCtx.audioWorklet.addModule('/audio-worklets/pcm-capture.js') }
        catch (err) {
          // Worklet falló a cargar — log y desistir. Sin STT pero el
          // resto del panel sigue funcional.
          // eslint-disable-next-line no-console
          console.warn('[STT] no se pudo cargar el AudioWorklet:', err)
          try { myCtx.close() } catch (_) {}
          if (this.sttCtx === myCtx) this.sttCtx = null
          return
        }

        // Si durante el await corrió _detachRxCapture (o un re-attach), this.sttCtx
        // ya no es myCtx: cerrar el nuestro y salir sin tocar el contexto ajeno
        // (evita el TypeError de createMediaStreamSource sobre un ctx cerrado).
        if (this.sttCtx !== myCtx) {
          try { myCtx.close() } catch (_) {}
          return
        }

        this.sttSource = myCtx.createMediaStreamSource(stream)
        this.sttWorklet = new AudioWorkletNode(myCtx, 'pcm-capture')
        this.sttSource.connect(this.sttWorklet)
        // NO conectar al destination — solo capturamos, no reproducimos
        // (eso ya lo hace remoteAudioEl).

        var self2 = this
        this.sttWorklet.port.onmessage = function(e) {
          self2._handleRxFrame(e.data)
        }
        // eslint-disable-next-line no-console
        console.log('[STT] RX capture enganchada al stream remoto del peer')
      } catch (err) {
        // createMediaStreamSource / new AudioWorkletNode lanzaron → cerrar el
        // AudioContext para no acumular contextos (el browser limita a ~6/tab).
        // eslint-disable-next-line no-console
        console.warn('[STT] attach RX capture failed:', err)
        try { if (this.sttSource) this.sttSource.disconnect() } catch (_) {}
        try { if (myCtx) myCtx.close() } catch (_) {}
        this.sttSource = null
        this.sttWorklet = null
        if (this.sttCtx === myCtx) this.sttCtx = null
      }
    },

    // Callback que recibe cada chunk Float32 (256ms a 16kHz) del worklet.
    // Implementa el VAD: ventana RMS, umbral 0.02, tail-off 600ms.
    _handleRxFrame(float32Buf) {
      if (!float32Buf || !float32Buf.length) return
      // Ignorar frames que lleguen tras el detach (mensajes ya encolados en el
      // port del worklet): sin esto, _handleRxFrame podía re-activar
      // sttBurstActive después de soltar todos los recursos.
      if (!this.sttWorklet || !this.sttCtx) return
      var rms = rmsOf(float32Buf)
      var now = new Date().getTime()
      var VAD_THRESHOLD = 0.02
      var VAD_TAIL_MS = 600 // ms de silencio para considerar fin de burst

      if (rms > VAD_THRESHOLD) {
        this.sttLastVoiceAt = now
        // Peer hablando (audio RX entrante) → marca reactiva con "hangover" de
        // 1.2s para inhabilitar el PTT del operador mientras el radio habla
        // (half-duplex). El backend TETRA NO emite RemoteSpeaking, así que lo
        // derivamos del propio audio del peer (el mismo VAD que alimenta el STT).
        this.peerAudioActive = true
        if (this._peerAudioTimer) clearTimeout(this._peerAudioTimer)
        this._peerAudioTimer = setTimeout(() => { this.peerAudioActive = false }, 1200)
        if (!this.sttBurstActive) {
          this.sttBurstActive = true
          this.sttBurstSamples = []
          this._rxBurstSpeaker = null
        }
      }
      if (this.sttBurstActive) {
        // Capturar QUIÉN habla MIENTRAS habla: el backend pone remoteSpeaker en
        // el evento 'floor' RemoteSpeaking y lo borra al cesar. Si lo leyéramos
        // recién en _pushRxEntry (tras la ráfaga + STT) ya sería null → caería
        // al nombre del canal (p.ej. "Despacho C5") en vez del id del radio.
        const spk = this.activeRadioCall && this.activeRadioCall.remoteSpeaker
        if (spk) this._rxBurstSpeaker = spk
        this.sttBurstSamples.push(float32Buf)
        // Limita el burst a ~30s para no acumular memoria si el VAD
        // queda atorado por ruido continuo. Whisper acepta hasta 30s
        // por archivo en chunks default.
        var totalSamples = 0
        for (var i = 0; i < this.sttBurstSamples.length; i++) {
          totalSamples += this.sttBurstSamples[i].length
        }
        var burstSeconds = totalSamples / 16000
        if (burstSeconds >= 30) {
          this._flushBurstToSTT()
          return
        }
      }
      if (this.sttBurstActive && (now - this.sttLastVoiceAt) > VAD_TAIL_MS) {
        this._flushBurstToSTT()
      }
    },

    _flushBurstToSTT() {
      if (!this.sttBurstActive) return
      var samples = this.sttBurstSamples
      const speaker = this._rxBurstSpeaker
      this.sttBurstActive = false
      this.sttBurstSamples = []
      this._rxBurstSpeaker = null
      // Descartar bursts muy cortos (probablemente ruido o un beep).
      var totalSamples = 0
      for (var i = 0; i < samples.length; i++) totalSamples += samples[i].length
      if (totalSamples < 8000) return  // <0.5s a 16kHz
      this._sendBurstToSTT(samples, totalSamples, speaker)
    },

    async _sendBurstToSTT(samples, totalSamples, speaker) {
      var durationMs = Math.round((totalSamples / 16000) * 1000)
      var blob
      try {
        blob = float32ToWav(concatFloat32(samples), 16000)
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[STT] encode WAV failed:', err)
        // Aun sin poder codificar, registramos que el peer transmitió.
        this._pushRxEntry(null, durationMs, speaker)
        return
      }
      this.sttTranscribing = true
      var text = null
      try {
        var resp = await sttService.transcribeBlob(blob, { language: 'es' })
        text = ((resp && resp.text) || '').trim() || null
        // El backend respondió → el servicio STT está disponible.
        this.sttRxUnavailable = false
        // eslint-disable-next-line no-console
        console.log('[STT] RX burst', durationMs, 'ms →', text ? ('"' + text + '"') : 'sin texto')
      } catch (err) {
        // POST falló (red/404/502) → servicio STT no disponible. El banner
        // "Transcripción RX no disponible" se enciende.
        this.sttRxUnavailable = true
        // eslint-disable-next-line no-console
        console.warn('[STT] transcribe RX falló (' + durationMs + 'ms):', err && err.message)
      } finally {
        this.sttTranscribing = false
      }
      // SIEMPRE creamos la entry RX: con texto si STT lo dio, o como
      // "audio recibido · transcripción pendiente" si STT está caído/vacío.
      // Así el operador SIEMPRE ve que el peer transmitió, aunque el backend
      // de transcripción no esté disponible (antes no aparecía nada → "no
      // funciona"). El push también persiste en IndexedDB ligado al callId.
      this._pushRxEntry(text, durationMs, speaker)
    },

    _pushRxEntry(text, durationMs, capturedSpeaker) {
      // El id del radio que habló se captura DURANTE la ráfaga (_rxBurstSpeaker):
      // al momento del push el backend ya borró remoteSpeaker, que caería al
      // nombre del canal. Fallbacks: remoteSpeaker actual → peerName → 'Peer'.
      // En modo CANAL (grupo) NO caemos al peerName: ese es el nombre del
      // CANAL (p.ej. "Despacho C5"), no el del radio que habló. Hoy el backend
      // TETRA no manda el id del radio, así que usamos un genérico "Radio". En
      // modo device, peerName SÍ es el dispositivo correcto.
      var speaker = capturedSpeaker
        || (this.activeRadioCall && this.activeRadioCall.remoteSpeaker)
        || (this.isChannelMode ? 'Radio' : (this.activeRadioCall && this.activeRadioCall.peerName))
        || 'Radio'
      this.$store.dispatch('map-v2/pushRadioActivity', {
        id: 'rx-' + new Date().getTime() + '-' + Math.round(durationMs) + '-' + (this._radioSeq++),
        callId: this.activeRadioCall ? this.activeRadioCall.callId : null,
        direction: 'RX',
        channelId: this.radioActiveChannelIds[0] || null,
        channelName: this.activeChannelObjs[0] ? this.activeChannelObjs[0].shortName : '',
        callerId: speaker,
        callerName: speaker,
        durationMs: durationMs,
        timestamp: new Date(),
        transcript: text || null,
      })
    },

    _detachRxCapture() {
      // Al cerrar this.sttCtx aquí, un _attachRxCapture en vuelo detecta que
      // this.sttCtx !== su myCtx tras el await y aborta sin dereferenciar nada.
      if (this.sttBurstActive) {
        // Si quedaba un burst en vuelo, lo flusheamos antes de cerrar.
        this._flushBurstToSTT()
      }
      try { if (this.sttWorklet) this.sttWorklet.disconnect() } catch (_) {}
      try { if (this.sttSource) this.sttSource.disconnect() } catch (_) {}
      try { if (this.sttCtx) this.sttCtx.close() } catch (_) {}
      this.sttWorklet = null
      this.sttSource = null
      this.sttCtx = null
      this.sttBurstSamples = []
      this.sttBurstActive = false
      this.sttLastVoiceAt = 0
      this.sttTranscribing = false
      if (this._peerAudioTimer) { clearTimeout(this._peerAudioTimer); this._peerAudioTimer = null }
      this.peerAudioActive = false
    },

    // Foto del elemento que transmitió, o '' para que el avatar caiga a las
    // iniciales. Cadena: identificador del hablante → radio en el catálogo de
    // devices → attributes.conductor → ficha del elemento → foto.
    //
    // Devuelve '' en silencio cuando algo de esa cadena no resuelve, que es lo
    // normal: en modo canal el backend no identifica al radio que habló.
    //
    // Se cachea por entrada porque esto corre en el render de una lista que se
    // repinta con cada ráfaga de audio; sin caché recorrería el catálogo de
    // devices por mensaje y por frame.
    speakerPhoto(entry) {
      if (!entry || entry.direction === 'TX') return ''
      // this.driverDirTick se lee para re-renderizar cuando el directorio
      // termina de cargar (módulo async, no reactivo).
      this.driverDirTick // eslint-disable-line no-unused-expressions
      if (!this._speakerPhotoCache) this._speakerPhotoCache = {}
      const cache = this._speakerPhotoCache
      if (Object.prototype.hasOwnProperty.call(cache, entry.id)) return cache[entry.id]

      const conductorId = findConductorByRadioIdent(entry.callerId || entry.callerName)
      if (!conductorId) { cache[entry.id] = ''; return '' }
      const info = getDriverInfoById(conductorId)
      if (!info) {
        // El directorio aún no está: se pide una vez y NO se cachea, para que
        // el siguiente render resuelva con los datos ya cargados.
        this._ensureDriverDirForSpeakers()
        return ''
      }
      const url = driverPhotoUrl(info.photo)
      cache[entry.id] = url
      return url
    },

    // La foto de un elemento puede estar registrada pero faltar en disco.
    // Se marca esa entrada como sin foto para volver a las iniciales en vez
    // de dejar el hueco de una imagen rota.
    onSpeakerPhotoError(entry) {
      if (!entry) return
      if (!this._speakerPhotoCache) this._speakerPhotoCache = {}
      this._speakerPhotoCache[entry.id] = ''
      this.driverDirTick++
    },

    _ensureDriverDirForSpeakers() {
      if (isDriverDirectoryLoaded() || this._loadingDriverDir) return
      this._loadingDriverDir = true
      const self = this
      ensureDriverDirectory().then(function() {
        self._loadingDriverDir = false
        self.driverDirTick++
      })
    },

    getInitials(name) {
      if (!name) return '?'
      var parts = String(name).split(/[\s\-_]+/).filter(Boolean)
      if (!parts.length) return '?'
      if (parts.length === 1) return parts[0].substr(0, 2).toUpperCase()
      return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase()
    },

    // Botón "Probar micrófono" del indicador del header. Dispara el prompt
    // nativo si el browser nunca pidió permiso, o muestra error si fue
    // denied. Activa el meter por 2.5s para feedback visual.
    async onTestMic() {
      // Bloquear el test durante transmisión activa. micLevelMeter.start
      // abre un getUserMedia paralelo + AudioContext que podría chocar
      // con el stream del bridge si se inicia mientras está transmitiendo.
      if (this.micTesting || this.radioTalking) return
      this.micTesting = true
      this.micErr = null
      var res = await requestMicPermission()
      this.micPermission = res.state
      this.micErr = res.error || null
      if (res.state === 'granted') {
        // El permiso recién concedido habilita las etiquetas de dispositivos.
        this._loadAudioDevices()
        var self = this
        var ok = await micLevelMeter.start(function(lvl, bands) {
          self.micLevel = lvl
          self.micBands = bands || []
        }, { bandCount: this.waveformBarCount })
        if (ok) {
          setTimeout(function() {
            micLevelMeter.stop()
            self.micLevel = 0
            self.micBands = []
            self.micTesting = false
          }, 2500)
          return
        }
      }
      this.micTesting = false
    },

    // ─── Dispositivos de audio (mic/altavoz) — portado de RadioCallBar ───
    // El bridge (radioAudioBridge) lista, aplica y persiste; funciona también
    // a mitad de llamada (replaceTrack / setSinkId) y pre-llamada (solo guarda
    // la preferencia en localStorage, que open() reaplica en la próxima llamada).
    async _loadAudioDevices() {
      try {
        var list = await radioAudioBridge.listDevices()
        this.audioDevices = (list && list.mics) ? list : { mics: [], speakers: [] }
      } catch (e) { this.audioDevices = { mics: [], speakers: [] } }
      // Mic actual: del track activo si hay llamada, si no del localStorage.
      var stream = radioAudioBridge.localStream
      if (stream && stream.getAudioTracks) {
        var track = stream.getAudioTracks()[0]
        if (track) this.currentMicId = track.getSettings().deviceId || this.currentMicId
      } else if (!this.currentMicId) {
        this.currentMicId = window.localStorage.getItem('radioCallBar.deviceMic') || ''
      }
      if (!this.currentSpeakerId) {
        this.currentSpeakerId = window.localStorage.getItem('radioCallBar.deviceSpeaker') || ''
      }
    },
    async pickMic(deviceId) {
      this.currentMicId = deviceId
      this.micMenuOpen = false
      try { await radioAudioBridge.setMicDevice(deviceId) } catch (e) { /* ignore */ }
    },
    async pickSpeaker(deviceId) {
      this.speakerMenuOpen = false
      try {
        var ok = await radioAudioBridge.setSpeakerDevice(deviceId)
        if (ok) this.currentSpeakerId = deviceId
      } catch (e) { /* ignore */ }
    },
    // Dispara el prompt de permiso de Chrome (una sola vez). Tras concederlo,
    // refresca el estado (sincroniza el indicador del header) y carga los
    // dispositivos con sus etiquetas reales.
    async requestAudioPermission() {
      try { await radioAudioBridge.requestMicPermission() } catch (e) { /* ignore */ }
      try { this.micPermission = await getMicPermissionState() } catch (e) { /* ignore */ }
      this._loadAudioDevices()
    },
    // Clic en un radio de la lista: solo selecciona (emite el monitor para que
    // MapV2 arme el target via buildRadioTarget). La llamada arranca con PTT.
    onSelectRadio: function(r) {
      if (!r || !r.callable) return
      this.$emit('select-radio', r.monitor)
    },

    // Nombre a mostrar: 1) match exacto por id, 2) item 'default' de Chrome,
    // 3) primer item con label, 4) fallback genérico.
    _resolveDeviceLabel(list, id, fallback) {
      if (!Array.isArray(list) || list.length === 0) return fallback
      var exact = id ? list.find(function(d) { return d.deviceId === id }) : null
      if (exact && exact.label) return exact.label
      var def = list.find(function(d) { return d.deviceId === 'default' })
      if (def && def.label) return def.label
      var first = list.find(function(d) { return d.label })
      if (first) return first.label
      return fallback
    },
  },
}
</script>

<style scoped>
.radio-tetra-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: var(--kln-bg-surface);
  border-right: 1px solid var(--kln-border-dim, rgba(255,255,255,0.08));
  color: var(--kln-text);
  font-size: 12px;
  overflow: hidden;
}

/* ───── Header ───── */
.rt-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 10px;
  border-bottom: 1px solid var(--kln-border-dim, rgba(255,255,255,0.08));
  flex: 0 0 auto;
}
.rt-header__title {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1 1 auto;
  min-width: 0;
}
.rt-header__actions {
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 0 0 auto;
}

/* Indicador del mic — clickeable para "Probar micrófono". Cambia de color
   según permiso: verde granted, ámbar prompt, rojo denied, gris unsupported. */
.rt-mic-indicator {
  width: 22px;
  height: 22px;
  border-radius: 4px;
  border: 1px solid var(--kln-border-normal);
  background: var(--kln-bg-card);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--kln-text-muted);
  transition: all 0.12s;
  position: relative;
  padding: 0;
}
.rt-mic-indicator:hover {
  background: var(--kln-border-dim);
}
.rt-mic-indicator:disabled {
  cursor: wait;
  opacity: 0.7;
}
.rt-mic-indicator--granted {
  border-color: rgba(46, 204, 113, 0.45);
  background: rgba(46, 204, 113, 0.1);
  color: #2ECC71;
}
.rt-mic-indicator--prompt {
  border-color: rgba(243, 156, 18, 0.45);
  background: rgba(243, 156, 18, 0.1);
  color: var(--kln-warning, #F39C12);
}
.rt-mic-indicator--denied {
  border-color: rgba(231, 76, 60, 0.45);
  background: rgba(231, 76, 60, 0.1);
  color: var(--kln-error, #E74C3C);
}
.rt-mic-indicator--unknown {
  /* default */
}
.rt-mic-indicator--no-speech {
  /* badge sutil de "no soporte de transcripción" */
  position: relative;
}
.rt-mic-indicator--no-speech::after {
  content: '!';
  position: absolute;
  top: -3px;
  right: -3px;
  width: 8px;
  height: 8px;
  background: var(--kln-warning, #F39C12);
  color: #000;
  border-radius: 50%;
  font-size: 8px;
  line-height: 8px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
}
.rt-mic-indicator__spin {
  position: absolute;
  inset: -2px;
  border: 2px solid transparent;
  border-top-color: var(--kln-accent, #16C4C7);
  border-radius: 6px;
  animation: rt-mic-spin 0.7s linear infinite;
}
@keyframes rt-mic-spin {
  to { transform: rotate(360deg); }
}
.rt-header__icon {
  flex: 0 0 auto;
}
.rt-header__text {
  font-weight: 600;
  font-size: 13px;
  color: var(--kln-text);
}
.rt-header__live {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 9px;
  letter-spacing: 0.5px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 8px;
  background: var(--kln-border-dim);
  color: var(--kln-text-muted);
  margin-left: 4px;
}
.rt-header__live--on {
  background: rgba(46, 204, 113, 0.15);
  color: #2ECC71;
}
.rt-header__live-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}
.rt-header__live--on .rt-header__live-dot {
  animation: rt-pulse-dot 1.4s ease-in-out infinite;
}
@keyframes rt-pulse-dot {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
}

/* ───── Mode tabs ───── */
.rt-modes {
  display: flex;
  gap: 4px;
  padding: 6px 8px;
  border-bottom: 1px solid var(--kln-border-dim, rgba(255,255,255,0.06));
  flex: 0 0 auto;
}
.rt-mode-tab {
  flex: 1 1 0;
  min-width: 0;
  background: transparent;
  border: 1px solid var(--kln-border-dim, rgba(255,255,255,0.08));
  border-radius: 4px;
  padding: 6px 4px;
  color: var(--kln-text-muted);
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.rt-mode-tab:hover {
  background: var(--kln-border-dim);
  color: var(--kln-text);
}
.rt-mode-tab--active {
  background: rgba(var(--kln-accent-rgb, 22, 196, 199), 0.12);
  border-color: var(--kln-accent, #16C4C7);
  color: var(--kln-accent, #16C4C7);
  font-weight: 600;
}

/* ───── Target / channel selectors ───── */
.rt-target {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  background: var(--kln-bg-card);
  border-bottom: 1px solid var(--kln-border-dim, rgba(255,255,255,0.06));
  flex: 0 0 auto;
}
.rt-target__icon {
  width: 28px; height: 28px;
  display: flex; align-items: center; justify-content: center;
  border-radius: 6px;
  background: rgba(var(--kln-accent-rgb, 22, 196, 199), 0.1);
  flex: 0 0 auto;
}
.rt-target__body {
  flex: 1 1 auto;
  min-width: 0;
}
.rt-target__name {
  font-weight: 600;
  font-size: 12px;
  color: var(--kln-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.rt-target__meta {
  font-size: 10px;
  color: var(--kln-text-dim);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.rt-target__empty {
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  min-width: 0;
}
.rt-target__empty-body {
  min-width: 0;
}
.rt-target__empty-title {
  font-size: 12px;
  font-weight: 600;
  color: var(--kln-text);
}
.rt-target__empty-hint {
  font-size: 10px;
  color: var(--kln-text-muted);
  line-height: 1.3;
}

.rt-channel-row {
  padding: 8px 10px;
  border-bottom: 1px solid var(--kln-border-dim, rgba(255,255,255,0.06));
  flex: 0 0 auto;
}
.rt-channel-select { }

/* ───── Multi list (grid 2 columnas) ─────
   Cards compactas con shortName + lock + fullName + status + activeUsers.
   El operador puede seleccionar 1 o N canales con un toggle global.
   IMPORTANTE: flex 0 1 auto (shrink=1) + max-height 200px. Antes era
   0 0 auto + 38vh y se comía todo el espacio del panel — el chat
   (flex 1 1 0 min-height 140px) era empujado fuera del viewport por
   el overflow:hidden del panel. Ahora el motor flex puede comprimir
   esta lista cuando el panel está apretado y el chat queda visible.
*/
.rt-multi-list {
  padding: 6px 8px 8px;
  border-bottom: 1px solid var(--kln-border-dim, rgba(255,255,255,0.06));
  flex: 0 1 auto;
  min-height: 80px;
  max-height: 200px;
  overflow-y: auto;
}
.rt-multi-list__hint {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 9px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--kln-text-dim);
  padding: 4px 4px 6px;
}
.rt-multi-list__hint strong {
  color: var(--kln-accent, #16C4C7);
  font-weight: 700;
}
.rt-multi-list__toggle-all {
  background: transparent;
  border: 1px solid rgba(var(--kln-accent-rgb, 22, 196, 199), 0.35);
  color: var(--kln-accent, #16C4C7);
  font-size: 9px;
  font-weight: 600;
  letter-spacing: 0.4px;
  padding: 2px 6px;
  border-radius: 3px;
  cursor: pointer;
  transition: background 0.1s;
  white-space: nowrap;
}
.rt-multi-list__toggle-all:hover {
  background: rgba(var(--kln-accent-rgb, 22, 196, 199), 0.1);
}

.rt-multi-list__grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 4px;
}
.rt-multi-card {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 6px 6px 6px 4px;
  border: 1px solid var(--kln-border-dim);
  background: var(--kln-bg-card);
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.12s;
  min-width: 0;
}
.rt-multi-card:hover {
  background: var(--kln-bg-overlay);
  border-color: var(--kln-border-normal);
}
.rt-multi-card--checked {
  background: rgba(var(--kln-accent-rgb, 22, 196, 199), 0.1);
  border-color: rgba(var(--kln-accent-rgb, 22, 196, 199), 0.4);
}
.rt-multi-card--checked:hover {
  background: rgba(var(--kln-accent-rgb, 22, 196, 199), 0.14);
}
.rt-multi-card--disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.rt-multi-card__row {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
}
.rt-multi-card__short {
  font-size: 11px;
  font-weight: 700;
  color: var(--kln-text);
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.rt-multi-card--checked .rt-multi-card__short {
  color: var(--kln-accent, #16C4C7);
}
.rt-multi-card__lock {
  flex: 0 0 auto;
}
.rt-multi-card__full {
  font-size: 9px;
  color: var(--kln-text-muted);
  padding-left: 18px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.2;
}
.rt-multi-card__meta {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 9px;
  color: var(--kln-text-dim);
  padding-left: 18px;
}
.rt-multi-card__users {
  font-weight: 500;
}

/* Type badge (mcx/tetra) en la card del group. Indica el provider del
   backend. Ambos (mcx y tetra) son seleccionables; las cards solo se
   atenúan durante una llamada activa contra otro canal. */
.rt-multi-card__type {
  font-size: 8px;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: var(--kln-text-muted);
  background: var(--kln-border-dim);
  padding: 1px 4px;
  border-radius: 2px;
  flex: 0 0 auto;
}
.rt-multi-card__gid {
  font-family: 'SF Mono', Menlo, monospace;
  color: var(--kln-text-dim);
  font-size: 9px;
}

.rt-multi-list__status {
  font-size: 10px;
  font-weight: 600;
  color: var(--kln-accent, #16C4C7);
  display: inline-flex;
  align-items: center;
  gap: 3px;
  text-transform: none;
  letter-spacing: 0;
}
.rt-multi-list__empty {
  padding: 16px;
  text-align: center;
  font-size: 10px;
  color: var(--kln-text-dim);
  font-style: italic;
}

/* Botón Colgar en la zona del PTT — visible solo durante callActive */
.rt-hangup-btn {
  margin-top: 6px;
  letter-spacing: 0.3px;
}

/* ───── Channel state badge colors ───── */
.rt-channel-status { font-size: 14px; line-height: 1; }
.rt-channel-status--available { color: #2ECC71; }
.rt-channel-status--busy      { color: var(--kln-warning, #F39C12); }
.rt-channel-status--offline   { color: var(--kln-text-dim); }

/* ───── Participants ───── */
.rt-participants {
  padding: 6px 10px;
  border-bottom: 1px solid var(--kln-border-dim, rgba(255,255,255,0.06));
  flex: 0 0 auto;
}
.rt-participants__hint {
  font-size: 9px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--kln-text-dim);
  margin-bottom: 4px;
}
.rt-participants__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
.rt-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: rgba(var(--kln-accent-rgb, 22, 196, 199), 0.12);
  border: 1px solid rgba(var(--kln-accent-rgb, 22, 196, 199), 0.3);
  border-radius: 10px;
  padding: 2px 6px 2px 4px;
  font-size: 10px;
  color: var(--kln-text);
}
.rt-chip__close {
  cursor: pointer;
  opacity: 0.5;
  transition: opacity 0.1s;
}
.rt-chip__close:hover { opacity: 1; }

/* ───── Channel state row ───── */
.rt-channel-state {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  font-size: 10px;
  color: var(--kln-text-muted);
  border-bottom: 1px solid var(--kln-border-dim, rgba(255,255,255,0.06));
  flex: 0 0 auto;
}
.rt-channel-state__dot {
  width: 8px; height: 8px; border-radius: 50%;
}
.rt-channel-state__dot--available { background: #2ECC71; box-shadow: 0 0 6px rgba(46,204,113,0.6); }
.rt-channel-state__dot--busy      { background: var(--kln-warning, #F39C12); animation: rt-pulse-dot 0.7s ease-in-out infinite; }
.rt-channel-state__dot--offline   { background: var(--kln-error, #E74C3C); }
.rt-channel-state__dot--idle      { background: var(--kln-border-normal); }
.rt-channel-state__dot--ready     { background: var(--kln-accent, #16C4C7); box-shadow: 0 0 6px rgba(22,196,199,0.6); }
.rt-channel-state__dot--transmit  { background: var(--kln-accent, #16C4C7); animation: rt-pulse-dot 0.6s ease-in-out infinite; box-shadow: 0 0 8px rgba(22,196,199,0.8); }
.rt-channel-state__dot--remote    { background: #E0E6ED; animation: rt-pulse-dot 0.9s ease-in-out infinite; }
.rt-channel-state__label {
  font-weight: 600;
  color: var(--kln-text);
}
.rt-channel-state__divider {
  color: var(--kln-border-normal);
}
.rt-channel-state__activity { font-style: italic; }

/* ───── Live transcript ───── */
.rt-live-transcript {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 10px;
  background: linear-gradient(180deg, rgba(var(--kln-accent-rgb, 22, 196, 199), 0.08), rgba(var(--kln-accent-rgb, 22, 196, 199), 0.02));
  border-bottom: 1px solid var(--kln-border-dim, rgba(255,255,255,0.06));
  border-left: 2px solid var(--kln-accent, #16C4C7);
  flex: 0 0 auto;
}
.rt-live-transcript__head {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 9px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--kln-accent, #16C4C7);
  font-weight: 700;
}
.rt-live-transcript__dot {
  width: 6px; height: 6px;
  border-radius: 50%;
  background: var(--kln-accent, #16C4C7);
  animation: rt-pulse-dot 0.9s ease-in-out infinite;
  flex: 0 0 auto;
}
.rt-live-transcript__label {
  flex: 0 0 auto;
}
.rt-live-transcript__warn {
  margin-left: auto;
  font-size: 9px;
  font-weight: 600;
  color: var(--kln-warning, #F39C12);
  text-transform: none;
  letter-spacing: 0;
}
.rt-live-transcript__body {
  font-size: 12px;
  line-height: 1.45;
  color: var(--kln-text);
  max-height: 30vh;       /* hasta 30% del viewport, después scroll interno */
  min-height: 36px;
  overflow-y: auto;
  word-wrap: break-word;
  overflow-wrap: break-word;
  white-space: pre-wrap;  /* preserva saltos de línea naturales del speech */
}
.rt-live-transcript__final {
  font-weight: 500;
}
.rt-live-transcript__interim {
  color: var(--kln-text-muted);
  font-style: italic;
}
.rt-live-transcript__hint {
  color: var(--kln-text-dim);
  font-style: italic;
  font-size: 11px;
}

.rt-mic-denied {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  background: rgba(243, 156, 18, 0.08);
  border-bottom: 1px solid var(--kln-border-dim, rgba(255,255,255,0.06));
  border-left: 2px solid var(--kln-warning, #F39C12);
  font-size: 11px;
  color: var(--kln-text);
  flex: 0 0 auto;
}
.rt-mic-denied__retry {
  background: transparent;
  border: 1px solid var(--kln-warning, #F39C12);
  color: var(--kln-warning, #F39C12);
  padding: 2px 8px;
  border-radius: 3px;
  font-size: 10px;
  font-weight: 600;
  cursor: pointer;
  margin-left: auto;
  transition: background 0.12s;
}
.rt-mic-denied__retry:hover {
  background: rgba(243, 156, 18, 0.15);
}

/* Hint del Time-Out Timer: misma estética ámbar que rt-mic-denied. Aparece
   junto al PTT cuando el TOT corta la TX por exceder el tiempo máximo. */
.rt-tot-cutoff {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  background: rgba(243, 156, 18, 0.08);
  border-bottom: 1px solid var(--kln-border-dim, rgba(255,255,255,0.06));
  border-left: 2px solid var(--kln-warning, #F39C12);
  font-size: 11px;
  color: var(--kln-text);
  flex: 0 0 auto;
}

/* Banner: el servicio STT del RX no responde. Mismo lenguaje visual que
   rt-mic-denied (ámbar), arriba del chat para que sea evidente que el peer
   se recibe pero no se transcribe. */
.rt-rx-unavailable {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  background: rgba(243, 156, 18, 0.08);
  border-top: 1px solid var(--kln-border-dim, rgba(255,255,255,0.06));
  border-left: 2px solid var(--kln-warning, #F39C12);
  font-size: 11px;
  color: var(--kln-text);
  flex: 0 0 auto;
}

/* (mic level rail viejo eliminado — ahora el feedback visual del mic vive
   en el waveform equalizer debajo del botón PTT) */

/* ───── PTT zone ─────
   IMPORTANTE: flex: 0 0 auto (no grow) — antes era 1 0 auto y se comía
   todo el espacio sobrante del panel, dejando al chat sin altura. Ahora
   el PTT zone se queda en su tamaño natural (~180px) y el chat (flex 1 1 0)
   ocupa el resto del slot vertical.
*/
.rt-ptt-zone {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 12px 12px 10px;
  gap: 8px;
  flex: 0 0 auto;
  position: relative;
  background:
    radial-gradient(circle at 50% 50%, rgba(var(--kln-accent-rgb, 22, 196, 199), 0.04) 0%, transparent 70%);
}

.rt-ptt {
  position: relative;
  width: 100px;
  height: 100px;
  border-radius: 50%;
  border: 2px dashed rgba(var(--kln-accent-rgb, 22, 196, 199), 0.6);
  background: rgba(var(--kln-accent-rgb, 22, 196, 199), 0.08);
  color: var(--kln-text);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
  user-select: none;
  -webkit-user-select: none;
  -webkit-tap-highlight-color: transparent;
  /* Hold-to-talk: sin gestos del browser (scroll/zoom/long-press) mientras
     se mantiene presionado el PTT en pantallas táctiles. */
  touch-action: none;
  padding: 0;
  outline: none;
}
.rt-ptt:hover:not(:disabled) {
  background: rgba(var(--kln-accent-rgb, 22, 196, 199), 0.16);
  border-style: solid;
}
.rt-ptt:focus-visible {
  box-shadow: 0 0 0 3px rgba(var(--kln-accent-rgb, 22, 196, 199), 0.35);
}
.rt-ptt--talking {
  background: var(--kln-accent, #16C4C7);
  border-style: solid;
  border-color: var(--kln-accent, #16C4C7);
  box-shadow:
    0 0 0 6px rgba(var(--kln-accent-rgb, 22, 196, 199), 0.25),
    0 0 30px rgba(var(--kln-accent-rgb, 22, 196, 199), 0.45);
  animation: rt-ptt-pulse 1.6s ease-in-out infinite;
}
/* Estado ESCUCHANDO (call activa + floor=None) — listo para hablar.
   Border sólido cyan (en vez de dashed que es el "sin call") para que
   el operador vea que está EN llamada esperando su turno. No pulsa
   fuerte para no competir con el receiving. */
.rt-ptt--listening {
  border-style: solid;
  border-color: var(--kln-accent, #16C4C7);
  background: rgba(var(--kln-accent-rgb, 22, 196, 199), 0.05);
}

/* Estado RECIBIENDO (call activa + floor=RemoteSpeaking) — peer hablando.
   Verde pulsante para diferenciar claramente del transmitting cyan. El
   operador NO debe intentar transmitir mientras este estado está activo
   (en MCX algunos backends rechazan el demandFloor durante RemoteSpeaking). */
.rt-ptt--receiving {
  border-style: solid;
  border-color: #2ECC71;
  background: rgba(46, 204, 113, 0.12);
  animation: rt-ptt-receiving 1.4s ease-in-out infinite;
}
@keyframes rt-ptt-receiving {
  0%, 100% { box-shadow: 0 0 0 0 rgba(46, 204, 113, 0.5), 0 0 20px rgba(46, 204, 113, 0.2); }
  50%      { box-shadow: 0 0 0 12px rgba(46, 204, 113, 0), 0 0 30px rgba(46, 204, 113, 0.5); }
}

/* Estado CONECTANDO (alerting): pulso ámbar fuerte sincronizado con el
   ringback audible. El operador ve y oye que la llamada está marcando.
   Más prominente que el dot pequeño del estado anterior — el botón
   entero late con un halo expandiéndose cada 1.6s. */
.rt-ptt--connecting {
  border-color: var(--kln-warning, #F39C12);
  border-style: solid;
  background: rgba(243, 156, 18, 0.18);
  animation: rt-ptt-connecting 1.6s ease-in-out infinite;
}
@keyframes rt-ptt-connecting {
  0%, 100% {
    box-shadow:
      0 0 0 0 rgba(243, 156, 18, 0.6),
      0 0 20px rgba(243, 156, 18, 0.2);
    transform: scale(1);
  }
  50% {
    box-shadow:
      0 0 0 16px rgba(243, 156, 18, 0),
      0 0 30px rgba(243, 156, 18, 0.5);
    transform: scale(1.04);
  }
}

/* Estado PIDIENDO VOZ (floor=Demanding): retro INMEDIATA al clic mientras el
   server otorga el floor. Ámbar con pulso rápido (0.9s) para diferenciarlo del
   connecting (1.6s) y señalar "engaging" justo antes de transmitir. */
.rt-ptt--demanding {
  border-color: var(--kln-warning, #F39C12);
  border-style: solid;
  background: rgba(243, 156, 18, 0.14);
  animation: rt-ptt-demanding 0.9s ease-in-out infinite;
}
@keyframes rt-ptt-demanding {
  0%, 100% { box-shadow: 0 0 0 2px rgba(243, 156, 18, 0.5), 0 0 16px rgba(243, 156, 18, 0.25); }
  50%      { box-shadow: 0 0 0 9px rgba(243, 156, 18, 0), 0 0 24px rgba(243, 156, 18, 0.45); }
}

@keyframes rt-ptt-pulse {
  0%, 100% { box-shadow: 0 0 0 6px rgba(var(--kln-accent-rgb, 22, 196, 199), 0.25), 0 0 30px rgba(var(--kln-accent-rgb, 22, 196, 199), 0.45); }
  50%      { box-shadow: 0 0 0 14px rgba(var(--kln-accent-rgb, 22, 196, 199), 0.1), 0 0 50px rgba(var(--kln-accent-rgb, 22, 196, 199), 0.6); }
}
.rt-ptt--disabled {
  cursor: not-allowed;
  opacity: 0.45;
  border-color: var(--kln-border-normal);
  background: var(--kln-bg-card);
}

.rt-ptt__inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  position: relative;
  z-index: 2;
  pointer-events: none;
}
.rt-ptt__label {
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: var(--kln-text);
  text-align: center;
  line-height: 1.2;
  padding: 0 4px;
}

/* ───── Waveform equalizer ─────
   Banda horizontal de N barras delgadas que reaccionan al audio capturado
   por el AnalyserNode del mic. Cada barra mapea a una banda de frecuencia
   del FFT; la altura inline se actualiza cada rAF (~16ms).
   Si no hay datos reales (mic denied / no soporte), la clase --synthetic
   activa una animación CSS de sinusoide escalonada para mantener el feel.
*/
.rt-waveform {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  width: 100%;
  max-width: 280px;
  height: 56px;
  padding: 0 4px;
  margin-top: 4px;
  position: relative;
}
.rt-waveform__bar {
  flex: 1 1 auto;
  min-width: 2px;
  max-width: 5px;
  height: 8%;
  background: var(--kln-accent, #16C4C7);
  border-radius: 2px;
  /* Glow neón estilo dribbble */
  box-shadow:
    0 0 4px rgba(var(--kln-accent-rgb, 22, 196, 199), 0.7),
    0 0 8px rgba(var(--kln-accent-rgb, 22, 196, 199), 0.35);
  transition: height 60ms cubic-bezier(0.4, 0, 0.2, 1);
  will-change: height;
}

/* Onda ÚNICA recibiendo — cuando el audio activo es del peer (no tu mic),
   tintamos las barras de verde para que el operador distinga al toque
   "yo hablando" (accent) de "peer hablando" (verde). Es la MISMA onda;
   solo cambia el color según la fuente activa (activeBands). */
.rt-waveform--receiving .rt-waveform__bar {
  background: #2ECC71;
  box-shadow:
    0 0 4px rgba(46, 204, 113, 0.7),
    0 0 8px rgba(46, 204, 113, 0.35);
}

/* Modo sintético: cada barra anima con delay escalonado para crear el
   patrón visual de onda sinusoidal aunque no haya mic activo. */
.rt-waveform--synthetic .rt-waveform__bar {
  animation: rt-bar-pulse 0.9s ease-in-out infinite alternate;
  transition: none;
}
.rt-waveform--synthetic .rt-waveform__bar:nth-child(1)  { animation-delay: 0ms;   animation-duration: 0.7s; }
.rt-waveform--synthetic .rt-waveform__bar:nth-child(2)  { animation-delay: 60ms;  animation-duration: 0.9s; }
.rt-waveform--synthetic .rt-waveform__bar:nth-child(3)  { animation-delay: 120ms; animation-duration: 0.6s; }
.rt-waveform--synthetic .rt-waveform__bar:nth-child(4)  { animation-delay: 80ms;  animation-duration: 1.1s; }
.rt-waveform--synthetic .rt-waveform__bar:nth-child(5)  { animation-delay: 200ms; animation-duration: 0.8s; }
.rt-waveform--synthetic .rt-waveform__bar:nth-child(6)  { animation-delay: 30ms;  animation-duration: 0.9s; }
.rt-waveform--synthetic .rt-waveform__bar:nth-child(7)  { animation-delay: 160ms; animation-duration: 0.7s; }
.rt-waveform--synthetic .rt-waveform__bar:nth-child(8)  { animation-delay: 220ms; animation-duration: 1.0s; }
.rt-waveform--synthetic .rt-waveform__bar:nth-child(9)  { animation-delay: 100ms; animation-duration: 0.6s; }
.rt-waveform--synthetic .rt-waveform__bar:nth-child(10) { animation-delay: 180ms; animation-duration: 0.9s; }
.rt-waveform--synthetic .rt-waveform__bar:nth-child(11) { animation-delay: 50ms;  animation-duration: 0.8s; }
.rt-waveform--synthetic .rt-waveform__bar:nth-child(12) { animation-delay: 140ms; animation-duration: 1.0s; }
.rt-waveform--synthetic .rt-waveform__bar:nth-child(n+13) {
  animation-delay: calc(40ms * (var(--i, 13) - 12));
  animation-duration: 0.85s;
}
/* Fallback genérico para barras 13+ (los nth-child específicos lo pueden
   sobreescribir pero ESLint no acepta loop). El offset alterno da una
   variación natural sin necesidad de declarar cada índice. */
.rt-waveform--synthetic .rt-waveform__bar:nth-child(odd)  { animation-delay: 90ms; }
.rt-waveform--synthetic .rt-waveform__bar:nth-child(3n)   { animation-delay: 170ms; }
.rt-waveform--synthetic .rt-waveform__bar:nth-child(5n)   { animation-delay: 250ms; }
.rt-waveform--synthetic .rt-waveform__bar:nth-child(7n)   { animation-delay: 30ms;  animation-duration: 1.05s; }

@keyframes rt-bar-pulse {
  0%   { height: 14%; }
  25%  { height: 75%; }
  50%  { height: 28%; }
  75%  { height: 95%; }
  100% { height: 40%; }
}

.rt-ptt-hint {
  font-size: 10px;
  color: var(--kln-text-muted);
  display: inline-flex;
  align-items: center;
}
.rt-ptt-hint kbd {
  background: var(--kln-border-dim);
  border: 1px solid var(--kln-border-normal);
  border-radius: 3px;
  padding: 1px 4px;
  font-size: 9px;
  font-family: 'SF Mono', Menlo, monospace;
  margin: 0 2px;
  color: var(--kln-text);
}

/* ───── Chat unificado (estilo Slack) ─────
   Lista cronológica con mensajes TX y RX entremezclados. Cada mensaje:
   avatar 28px + body (head con nombre/canal/time + content con transcript).
   Más nuevo abajo, auto-scroll al recibir mensaje.
*/
.rt-chat {
  /* flex 1 1 0 (no auto): el chat reclama TODO el espacio sobrante del
     panel después de header/modes/target/transcript/PTT/footer. Antes
     era flex 1 1 auto y rt-ptt-zone era 1 0 auto → el PTT zone se
     comía el espacio elástico y el chat quedaba en altura 0. */
  flex: 1 1 0;
  min-height: 140px;
  overflow-y: auto;
  padding: 6px 0 4px;
  border-top: 1px solid var(--kln-border-dim, rgba(255,255,255,0.08));
  background: var(--kln-bg-surface, #161B22);
}
.rt-chat__empty {
  padding: 28px 16px;
  text-align: center;
  font-size: 11px;
  color: var(--kln-text-dim);
  font-style: italic;
  line-height: 1.6;
}
.rt-chat__msg {
  display: flex;
  gap: 8px;
  padding: 6px 10px 8px;
  transition: background 0.1s;
  border-bottom: 1px solid var(--kln-border-dim);
}
.rt-chat__msg:hover {
  background: var(--kln-bg-card);
}
.rt-chat__msg--tx {
  background: rgba(var(--kln-accent-rgb, 22, 196, 199), 0.03);
}
.rt-chat__msg--tx:hover {
  background: rgba(var(--kln-accent-rgb, 22, 196, 199), 0.06);
}

.rt-chat__avatar {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 28px;
  margin-top: 1px;
  font-weight: 700;
  font-size: 10px;
  letter-spacing: 0.4px;
  color: var(--kln-text);
  user-select: none;
  overflow: hidden; /* recorta el retrato al radio del avatar */
}

/* El retrato llena el avatar sin deformarse: las fotos de credencial vienen
   en proporciones distintas y `cover` las encuadra al centro. */
.rt-chat__avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.rt-chat__msg--tx .rt-chat__avatar {
  background: var(--kln-accent, #16C4C7);
  box-shadow: 0 0 0 1px rgba(var(--kln-accent-rgb, 22, 196, 199), 0.4);
  color: white;
}
.rt-chat__msg--rx .rt-chat__avatar {
  background: var(--kln-bg-overlay);
  color: var(--kln-text);
}
.rt-chat__avatar-init {
  font-family: 'SF Mono', Menlo, monospace;
}

.rt-chat__body {
  flex: 1 1 auto;
  min-width: 0;
}
.rt-chat__head {
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin-bottom: 2px;
  flex-wrap: wrap;
  line-height: 1.2;
}
.rt-chat__name {
  font-weight: 700;
  font-size: 12px;
  color: var(--kln-text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 60%;
}
.rt-chat__msg--tx .rt-chat__name {
  color: var(--kln-accent, #16C4C7);
}
.rt-chat__channel {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: 9px;
  color: var(--kln-text-dim);
  background: var(--kln-border-dim);
  padding: 1px 5px 1px 3px;
  border-radius: 3px;
  font-weight: 500;
  white-space: nowrap;
}
.rt-chat__channel-icon {
  font-size: 11px !important;
}
.rt-chat__time {
  font-family: 'SF Mono', Menlo, monospace;
  font-size: 9px;
  color: var(--kln-text-dim);
  margin-left: auto;
}
.rt-chat__dur {
  font-family: 'SF Mono', Menlo, monospace;
  font-size: 9px;
  color: var(--kln-text-dim);
}
.rt-chat__content {
  font-size: 12px;
  line-height: 1.4;
  color: var(--kln-text);
  word-wrap: break-word;
  overflow-wrap: break-word;
}
.rt-chat__placeholder {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: var(--kln-text-dim);
  font-style: italic;
  font-size: 11px;
}
.rt-chat__placeholder .v-icon {
  color: var(--kln-text-dim) !important;
}

/* ───── Footer ───── */
/* ─── Selección de dispositivos de audio (mic/altavoz) ─── */
.rt-audio {
  flex: 0 0 auto;
  padding: 6px 12px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  /* Mismo fondo que el footer para que ambos lean como la barra inferior. */
  background: rgba(0, 0, 0, 0.15);
  border-top: 1px solid var(--kln-border-dim);
}
.rt-audio__row {
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 3px 4px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 11px;
  color: var(--kln-text-muted);
  transition: background 0.12s, color 0.12s;
}
.rt-audio__row:hover {
  background: var(--kln-border-dim);
  color: var(--kln-text);
}
.rt-audio__row--disabled {
  cursor: not-allowed;
  opacity: 0.5;
}
.rt-audio__name {
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.rt-audio__permit {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 5px 8px;
  border-radius: 4px;
  border: 1px solid var(--kln-warning, #F39C12);
  background: rgba(243, 156, 18, 0.08);
  color: var(--kln-text);
  font-size: 11px;
  cursor: pointer;
}
.rt-audio__permit:hover { background: rgba(243, 156, 18, 0.15); }

.rt-audio__menu {
  width: 280px;
  background: var(--kln-bg-surface);
  color: var(--kln-text);
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.45);
  border: 1px solid var(--kln-border-normal);
}
.rt-audio__menu-title {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--kln-text-dim);
  padding: 8px 12px 4px;
  border-bottom: 1px solid var(--kln-border-dim);
}
.rt-audio__menu-list {
  max-height: 220px;
  overflow-y: auto;
}
.rt-audio__menu-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  font-size: 12px;
  cursor: pointer;
  border-bottom: 1px solid var(--kln-border-dim);
  transition: background 0.12s;
}
.rt-audio__menu-item:hover { background: var(--kln-border-dim); }
.rt-audio__menu-item.is-current {
  background: rgba(var(--kln-accent-rgb, 22, 196, 199), 0.1);
}
.rt-audio__menu-label {
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.rt-audio__check { flex-shrink: 0; }

.rt-radio-list { display: flex; flex-direction: column; gap: 8px; padding: 8px 0; }
.rt-radio-list__bar { display: flex; gap: 6px; align-items: center; }
.rt-radio-list__search {
  flex: 1 1 0; min-width: 0; background: transparent;
  border: 1px solid var(--kln-border, rgba(255,255,255,0.12));
  border-radius: 6px; color: var(--kln-text, #e8eef2);
  font-size: 12px; padding: 6px 8px; outline: none;
}
.rt-radio-list__search::placeholder { color: var(--kln-text-dim, #8a97a3); }
.rt-radio-list__refresh {
  background: transparent; border: 1px solid var(--kln-border, rgba(255,255,255,0.12));
  border-radius: 6px; padding: 4px 6px; cursor: pointer; color: var(--kln-text-dim);
}
.rt-radio-list__hint { font-size: 11px; color: var(--kln-text-dim); text-transform: uppercase; letter-spacing: 0.04em; }
.rt-radio-list__empty { font-size: 12px; color: var(--kln-text-dim); padding: 12px 4px; }
.rt-radio-list__grid { display: flex; flex-direction: column; gap: 4px; max-height: 320px; overflow-y: auto; }
.rt-radio-card {
  display: flex; align-items: center; gap: 8px; padding: 6px 8px;
  border: 1px solid transparent; border-radius: 6px; cursor: pointer;
}
.rt-radio-card:hover { border-color: var(--kln-accent, #3aa0ff); background: rgba(255,255,255,0.03); }
.rt-radio-card--disabled { opacity: 0.45; cursor: not-allowed; }
.rt-radio-card--disabled:hover { border-color: transparent; background: transparent; }
.rt-radio-card__dot { width: 8px; height: 8px; border-radius: 50%; flex: 0 0 auto; }
.rt-radio-card__dot.is-online { background: var(--kln-success, #2ecc71); }
.rt-radio-card__dot.is-offline { background: var(--kln-text-dim, #6b7680); }
.rt-radio-card__body { flex: 1 1 0; min-width: 0; }
.rt-radio-card__name { font-size: 12px; color: var(--kln-text, #e8eef2); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.rt-radio-card__meta { font-size: 10px; color: var(--kln-text-dim); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.rt-radio-card__issi { font-family: var(--kln-mono, monospace); }
.rt-radio-card__type {
  font-size: 9px; font-family: var(--kln-mono, monospace); letter-spacing: 0.06em;
  color: var(--kln-text-dim); border: 1px solid var(--kln-border, rgba(255,255,255,0.12));
  border-radius: 4px; padding: 1px 4px; flex: 0 0 auto;
}
</style>
